// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as Interfaces from '../../Interfaces/Interfaces';
import * as Enumerations from '../../Enumerations/Enumerations';
import * as Data from '../Data/WebData';
import * as BABYLON from 'babylonjs';
declare const InstallTrigger: any;
import * as FrustumMeshCoordinator from '../workers/frustumMeshCoordinator';
import * as FrustumMeshWorker from '../workers/frustumMeshWorker';
import * as FrustumRegionCoordinator from '../workers/frustumRegionCoordinator';
import * as FrustumStructures from '../workers/frustumStructures';
import * as FrustumRegionWorker from '../workers/frustumRegionWorker';
import DebugOptions from '../lodviewer/DebugOptions';
import * as Inovx from '@shared';
import GLBLoadWorker from '../workers/GLBLoadWorker';
import { ContentLayers } from '../../Interfaces/ViewerInterfaces';
import { QualityKey } from '../Data/WebData';
import { getCurrentWorkerAuthContext, handleWorkerAuthRefreshMessage } from '../workers/workerAuthHost';
import {
    AssetMemoryPressurePage,
    planAssetMemoryPressureResidency,
    planAssetPageAdmission,
} from '../../helpers/assetMemoryPressureModel';


type ColorType = "u8" | "f32";
type MeshQualityKey = "High" | "Medium" | "Low";
type MeshQualityProfile = {
    distanceMaxByLod: number[];
    requestedLodLevels: number[];
};
class DisplayManager implements Interfaces.Interfaces.IDisplayManager {
    private static readonly APPLY_XRAY_IN_WORKER_PARSE = false;
    public static LOG_GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS = false;
    public static GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS_INTERVAL_MS = 5000;
    private static readonly FALLBACK_MESH_PROFILES: Record<MeshQualityKey, MeshQualityProfile> = {
        High: { distanceMaxByLod: [2.5, 12.5, 25, 50, 100], requestedLodLevels: [1, 2, 3, 4, 5] },
        Medium: { distanceMaxByLod: [5, 12.5, 25, 100], requestedLodLevels: [1, 3, 4, 5] },
        Low: { distanceMaxByLod: [2.5, 25, 100], requestedLodLevels: [1, 3, 5] },
    };

    public showRegionBoxes: boolean = false;
    public lodDistances: number[] = [];
    private _meshLodDistances: number[] = [];
    private _meshRequestedLodLevels: number[] = [];
    private _memoryProfile: string = "";
    private _targettedMemoryProfile: string = "";
    private _meshMemoryProfile: string = "";
    private _targettedMeshMemoryProfile: string = "";
    private _voxelMMByIndex: number[] = [Number.NaN]; // 1-based; index 0 unused
    private _lodIndexByVoxelMM: Map<number, number> = new Map();
    private _contentLayers: ContentLayers = {
        assets: true,
        cad: true,
        pointCloud: false,
        mesh: true,
        positions360: false,
        scanPositions: false,
        colorScanPositions: false,
        blackAndWhiteScanPositions: false,
        imagePositions:false,
        assetTags: false,
        assemblies: false
    };

    private _assetLayerByNodeKey: { [nodeKey: string]: string } = {};
    private _overrideManager: Interfaces.Interfaces.IMaterialOverrideManager;
    private _boxMasterMesh: BABYLON.Mesh;
    private _pointMasterMesh: BABYLON.Mesh;
    private _regionMasterMesh: BABYLON.Mesh;
    // Shared viewer memory budget. Asset and region content both consume this total.
    private static MEMORY_PRESSURE_MEGABYTE_LIMIT = 1000;
    // Regions may consume at most this much of the shared viewer budget.
    private static MEMORY_REGION_PRESSURE_MEGABYTE_LIMIT = 500;

    private _wasmPath;
    private numMesh: number = 0;
    private octreeDepth = 6;
    private regionRows = 150;
    private regionCols = 150;
    private regionStacks = 4;
    private regionWidth = 20;
    private regionHeight = 10;
    private regionLength = 20;
    private regionLODLevels = 0;
    private _lodOneMemory = 0;
    private maxRegionMesh: number = 25000;
    private _boxMeshes: BABYLON.InstancedMesh[] = [];
    private _pointMeshes: BABYLON.InstancedMesh[] = [];
    private _regionMeshes: BABYLON.InstancedMesh[] = [];
    private _regionTreeNodes: FrustumStructures.TreeNode[] = [];
    private _regionFileNames: { [n: string]: string } = {};
    private _dataLength: number = 0;
    private _minNumMeshes: number = 0;
    private _numContent: number = 0;
    private _octreeBlocks: number = 1;

    private _lodPointSizes: number[] = [];
    private _regionDataLength: number = 0;

    private _regionCount: number = 1;
    private _visibleNodes: any;
    private _hiddenNodes: any;
    private _visibleRegionNodes: any[] = [];
    private _hiddenRegionNodes: any[] = [];
    private _regionVisibleNodes: FrustumStructures.TreeNode[] = [];
    private _pageLength: { [n: string]: number } = {};
    private _regionDataLengths: { [n: string]: { [n: string]: number; }; } = {};
    private _regionLodUrls: { [n: string]: { [n: string]: string; }; } = {};
    private _meshManager: Interfaces.Interfaces.IMeshManager;
    private _annotationManager: Interfaces.Interfaces.IAnnotationManager;

    private _selectionManager: Interfaces.Interfaces.ISelectionManager;
    private _displayedIndex = 0;
    private _displayEventHandler: Interfaces.Interfaces.IEventManager;
    private _processedWorkerEvents: Array<any> = new Array<any>();
    private _finWorkerEvents: Array<any> = new Array<any>();
    private _pool: Pool;
    private _numGraphics: number = 0;
    private _graphicsDisplayMode: number = 1;
    private _memoryDiposeMode: number = 1;
    private _regionTransformation: BABYLON.Vector3;
    private _memoryPressureHit = false;
    private _assetMemoryPressureHit = false;
    private _regionMemoryPressureHit = false;
    private _displaying = true;
    private _labelManager: Interfaces.Interfaces.ILabelManager;
    private _assemblyManager: Interfaces.Interfaces.IAssemblyManager;
    private _scanPositionManager: any;

    private _assetSetByRootBlock: { [rootBlockId: number]: string } = {};
    private _availableAssetLayers: Set<string> = new Set();
    private _assetSetManager: Interfaces.Interfaces.IAssetSetManager;
    public NumNodes: number = 0;

    private _copyNodeArray<T>(nodes: { length: number; [index: number]: T; } | null | undefined): T[] {
        const result: T[] = [];

        if (!nodes) {
            return result;
        }

        for (let i = 0; i < nodes.length; i++) {
            result.push(nodes[i]);
        }

        return result;
    }

    // Changed to map to GUID graphic list ids for page/block keys.
    private _displayDataCache: { [n: string]: { [n: string]: string; }; } = {};

    // 0 - unprocessed
    // 1 - tessellating/worker in progress
    // 2 - display data queued
    // 3 - mesh created, in scene
    private _displayDataStatus: { [n: string]: { [n: string]: number; }; } = {};

    private _displayRegionDataCache: { [n: string]: { [n: string]: string; }; } = {};

    private _displayRegionMetadataDataCache: { [n: string]: { [n: string]: string; }; } = {};

    // 0 - unprocessed
    // 1 - tessellating/worker in progress for meshes
    // 2 - tessellation/worker in progress for points
    // 3 - point cloud LOD(2) created, in scene
    // 4 - mesh created LOD(1), in scene
    private _displayRegionDataStatus: { [n: string]: { [n: string]: number; }; } = {};
    private _displayRegionDataProcessedStatus: { [n: string]: number } = {};
    private _displayRegionDataTypes: { [n: string]: { [n: string]: string; }; } = {};
    private _displayRequestGeneration: number = 0;
    private _displayPageRequestBatchSize: number = 10;
    private _pointCloudPageRequestBatchSize: number = 10;
    private _assetGeometryOwnership: Interfaces.Interfaces.IAssetGeometryOwnershipSnapshot = {
        revision: 0,
        ownerByAssetId: {},
        epochByAssetSetId: {},
        changedAssetSetIds: [],
        defaultUnownedGeometryHidden: false,
        fallbackSourceAssetSetIds: [],
    };
    // CAD is fallback 3D content: it is resident only while the All Assets
    // fallback is visible. Keep this separate from the user's CAD content
    // layer toggle so hiding All Assets does not alter UI configuration.
    private _allAssetsCadGeometryEnabled: boolean = true;
    private _filteredAssetIdsByPage: { [pageKey: string]: string[] } = {};
    private _lastGeneratedMeshLodMemoryDiagnosticUtc = 0;


    private _regionSegmentCount: { [n: string]: number; } = {};

    private _tasksForCompletePage: { [n: string]: { [n: string]: number; }; } = {};

    constructor(displayEventHander: Interfaces.Interfaces.IEventManager, meshManager: Interfaces.Interfaces.IMeshManager, annotationManager: Interfaces.Interfaces.IAnnotationManager, selectionManager: Interfaces.Interfaces.ISelectionManager, labelManager: Interfaces.Interfaces.ILabelManager, assemblyManager: Interfaces.Interfaces.IAssemblyManager, scanPositionManager: any, overrideManager: Interfaces.Interfaces.IMaterialOverrideManager, assetSetManager :Interfaces.Interfaces.IAssetSetManager,  dataRequestCallback : Function = null) {

        this._wasmPath = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/draco_decoder.wasm';

        this._assetSetManager = assetSetManager;
        this._meshManager = meshManager;
        this._displayEventHandler = displayEventHander;
        this._annotationManager = annotationManager;
        this._selectionManager = selectionManager;
        this._labelManager = labelManager;
        this._assemblyManager = assemblyManager;
        this._scanPositionManager = scanPositionManager;
        this._overrideManager = overrideManager;
        DisplayWorker.DataRequestCallback = dataRequestCallback;
        DisplayWorker.UseXRayMaterial = false;
        DisplayWorker.LodLevel = 0;
        DisplayWorker.TransformationX = 0;
        DisplayWorker.TransformationY = 0;
        DisplayWorker.TransformationZ = 0;
        this.initializeGeneratedMeshLodMemoryDiagnostics();
    }

    close() {

        if (this._pool != null) {
            this._pool.dispose();
            this._pool = null;
        }
    }

    dispose() {

        this.close();

        this._meshManager = null;
        this._annotationManager = null;
        this._processedWorkerEvents = null;
        this._displayEventHandler = null;
        this._pool = null;
    }

    public setMemoryCap(amount: number) {

        if (amount > 0) {
            DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT = amount;

            if (this._dataLength > this.getEffectiveAssetMemoryLimit()) {

                this.freeMemory(this._dataLength - this.getEffectiveAssetMemoryLimit());
            }
        }
    }

    public setXRayMode(enabled: boolean) {
        if (enabled) {
            this._overrideManager.setGlobalOverrideForSource("Xray", 1000, ["XRayMaterial"]);
        }
        else {

            this._overrideManager.clearGlobalOverrideForSource("Xray");
        }
    }

    public setMemoryLevel(level: Enumerations.MemoryMode) {
        void level;

        if (this._dataLength > this.getEffectiveAssetMemoryLimit()) {
            this.freeMemory(this._dataLength - this.getEffectiveAssetMemoryLimit());
        }
    }

    public getMemoryCap() {
        return DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT;
    }

    public getRegionDataLength() {
        return this._regionDataLength;
    }

    public getMemoryMinimum() {
        return DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT;
    }

    public getMemoryMaximum() {
        return DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT;
    }

    public increaseMemoryCap(increaseAmount: number) {
        if (increaseAmount > 0) {
            this.setMemoryCap(DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT + increaseAmount);
        }
    }

    public decreaseMemoryCap(decreaseAmount: number) {
        if (decreaseAmount > 0) {
            this.setMemoryCap(Math.max(1, DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT - decreaseAmount));
        }
    }
    public setMemoryProfile(profile: string) {
        // Only set the viewer memory profile IF point cloud data has not already been loaded. This is because we do NOT support
        // changing memory profile after load!.

        if (this.lodDistances.length > 0)
            return;

        this._memoryProfile = profile;
        this._targettedMemoryProfile = profile;
    }
    public getMemoryProfile() {

        return this._memoryProfile;
    }

    public getTargettedMemoryProfile() {
        return this._targettedMemoryProfile;
    }

    public setMeshMemoryProfile(profile: string) {
        const normalizedProfile = this.normalizeMeshMemoryProfile(profile) ?? this.mapPointProfileToMeshProfile(profile);
        this._targettedMeshMemoryProfile = normalizedProfile;
        this._meshMemoryProfile = normalizedProfile;
        this._meshLodDistances = DisplayManager.FALLBACK_MESH_PROFILES[normalizedProfile].distanceMaxByLod.slice();
        this._meshRequestedLodLevels = DisplayManager.FALLBACK_MESH_PROFILES[normalizedProfile].requestedLodLevels.slice();
    }

    public getMeshMemoryProfile() {
        return this._meshMemoryProfile || this.mapPointProfileToMeshProfile(this._memoryProfile);
    }

    public getTargettedMeshMemoryProfile() {
        return this._targettedMeshMemoryProfile || this.getMeshMemoryProfile();
    }

    public clearAllContent(resetWorkers: boolean = true): void {
        // 1) Stop caring about any in-flight worker results
        this.abandonCurrentWorkerResults();
        this._processedWorkerEvents = [];
        this._finWorkerEvents = [];
        this._tasksForCompletePage = {};

        // 2) Unload all “asset” pages (octree pages)
        if (this._displayDataStatus) {
            this._unloadAllAssetPages();
        }

        // 3) Unload all region content (CAD / mesh / point clouds)
        if (this._displayRegionDataProcessedStatus) {
            for (const regionId in this._displayRegionDataProcessedStatus) {
                this.unloadRegion(regionId);
            }
        }

        // 4) Dispose region/box/point meshes owned by DisplayManager
        if (this._boxMeshes) {
            for (const m of this._boxMeshes) {
                if (m) m.dispose();
            }
        }
        if (this._pointMeshes) {
            for (const m of this._pointMeshes) {
                if (m) m.dispose();
            }
        }
        if (this._regionMeshes) {
            for (const m of this._regionMeshes) {
                if (m) m.dispose();
            }
        }

        this._boxMeshes = [];
        this._pointMeshes = [];
        this._regionMeshes = [];
        this._regionTreeNodes = [];

        // 5) Clear caches & status dictionaries
        this._displayDataCache = {};
        this._displayDataStatus = {};
        this._displayRegionDataCache = {};
        this._displayRegionMetadataDataCache = {};
        this._displayRegionDataStatus = {};
        this._displayRegionDataProcessedStatus = {};
        this._displayRegionDataTypes = {};
        this._regionDataLengths = {};
        this._regionFileNames = {};
        this._regionSegmentCount = {};
        this._pageLength = {};
        this._regionLodUrls = {};
        this._regionDataLengths = {};
        this._tasksForCompletePage = {};

        // 6) Reset counters / memory accounting
        this._dataLength = 0;
        this._regionDataLength = 0;
        this._numContent = 0;
        this._regionCount = 1;
        this._octreeBlocks = 1;
        this.NumNodes = 0;
        this._displayedIndex = 0;
        this._memoryPressureHit = false;
        this._assetMemoryPressureHit = false;
        this._regionMemoryPressureHit = false;
        this._allAssetsCadGeometryEnabled = true;
        this._visibleNodes = null;
        this._hiddenNodes = null;

        this._voxelMMByIndex = [Number.NaN];
        this._lodIndexByVoxelMM.clear();
        this.lodDistances = [];
        this._meshLodDistances = [];
        this._meshRequestedLodLevels = [];
        this.regionLODLevels = 0;

        this._overrideManager?.clearSceneState();

        // 8) Reset worker state so abandoned tasks cannot keep the pool saturated.
        if (this._pool) {
            if (resetWorkers) {
                this._pool.dispose();
                this._pool = null;
            }
            else {
                this._pool.reset();
            }
        }

        // Force re-render to reflect the “no content” state
        if (this._meshManager) {
            this._meshManager.meshesDirty = true;
        }
    }
    public setupRegionOctree(
        dataRequestUrl: string,
        lodOptions: string | {
            Quality?: "Ultra" | "High" | "Medium" | "Low";
            profiles?: Record<"Ultra" | "High" | "Medium" | "Low", { voxelMM: number[]; distanceMaxByLod: number[] }>;
            CoverageScaleByVoxel?: Record<number, number>;
            Region_Megabyte_Memory_Limit?: number;
            Mode?: "ByTargetPx" | "ByFixedDistances";
            SelectedVoxelSizesMM?: number[];
            DistanceMaxByLod?: number[];
            TargetMaxPxByLod?: number[];
            [k: string]: any;
        },
        regionData: any,
        minBounds?: IVector2D,
        maxBounds?: IVector2D
    ): {} {
        DisplayWorker.OctreeRequestURL = dataRequestUrl;
        DisplayWorker.DisplayDecoderURL =
            Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + '/lod/';

        const opts = typeof lodOptions === 'string' ? JSON.parse(lodOptions) : lodOptions;
        return this.setupOctreeRegionData(regionData, opts, minBounds, maxBounds);
    }

    public setupOctree(
        dataRequestUrl: string,
        projectId: string,
        namedLists: any,
        displayData: {
            Extents: any;
            Children?: any[];
            assetLayerId?: string;
            ontologyId?: string;
        }[],
        minBounds?: IVector2D,
        maxBounds?: IVector2D
    ): {} {

        DisplayWorker.DisplayRequestURL = dataRequestUrl;
        DisplayWorker.ProjectID = projectId;
        DisplayWorker.AllNamedLists = namedLists;
        DisplayWorker.ActivatedNamedLists = namedLists;
        DisplayWorker.UseDefaultMaterial = false;

        const octrees = displayData;

        return this.setupOctreesData(octrees, minBounds, maxBounds);
    }

    public getAllSetID(): string {
        return this._assetSetManager.getAllSetID();
    }

    public setupOctreesData(
        octrees: {
            Extents: any;
            Children?: any[];
            assetLayerId?: string;
            ontologyId?: string;
        }[],
        minDisplayBounds?: IVector2D,
        maxDisplayBounds?: IVector2D
    ): {} {

        var that = this;
        var cb = function (workerNumber: number, event: MessageEvent) {

            that._processedWorkerEvents.push(event);
        };

        var numThreads = 8;
        if (this._pool == null)
            this._pool = new Pool(numThreads, true, cb);
        this._pool.setAssetGeometryOwnership(this._assetGeometryOwnership);

        this._assetLayerByNodeKey = {};

        var displayDataCache: { [n: string]: { [n: string]: string; }; } = {};
        var displayDataStatus: { [n: string]: { [n: string]: number; }; } = {};

        var rootBlocks = [];

        var displayBounds: BABYLON.BoundingBox = (minDisplayBounds != null && maxDisplayBounds != null) ?
        new BABYLON.BoundingBox(new BABYLON.Vector3(minDisplayBounds.X, Number.NEGATIVE_INFINITY, minDisplayBounds.Y), new BABYLON.Vector3(maxDisplayBounds.X, Number.POSITIVE_INFINITY, maxDisplayBounds.Y)) : null;

        for (let t = 0; t < octrees.length; t++) {

            const octreeMeta = octrees[t];

            const displayData = octreeMeta;

            const extentInfo = new BABYLON.BoundingBox(
                new BABYLON.Vector3(
                    displayData.Extents.Minimum.X / 1000,
                    displayData.Extents.Minimum.Z / 1000,
                    displayData.Extents.Minimum.Y / 1000
                ),
                new BABYLON.Vector3(
                    displayData.Extents.Maximum.X / 1000,
                    displayData.Extents.Maximum.Z / 1000,
                    displayData.Extents.Maximum.Y / 1000
                )
            );

            const rootBlock = new FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>(
                this._octreeBlocks,
                extentInfo.minimum,
                extentInfo.maximum,
                100,
                0,
                null
            );

            var assetLayerId = octreeMeta.assetLayerId;

            this._octreeBlocks++;

            this.constructOctreeOctants(
                rootBlock,
                displayData,
                displayDataCache,
                displayDataStatus,
                displayBounds,
                assetLayerId
            );

            rootBlocks.push(rootBlock);
        }

        this._displayDataCache = displayDataCache;
        this._displayDataStatus = displayDataStatus;

        return rootBlocks;
    }

    /**
     * Adds roots to the existing display maps without renumbering or replacing
     * roots already owned by the coordinator. Root block IDs remain stable for
     * the lifetime of the current viewer-data setup.
     */
    public appendOctreesData(
        octrees: {
            Extents: any;
            Children?: any[];
            assetLayerId?: string;
            ontologyId?: string;
        }[],
        minDisplayBounds?: IVector2D,
        maxDisplayBounds?: IVector2D
    ): {} {
        if (!octrees?.length) return [];

        const that = this;
        const cb = function (_workerNumber: number, event: MessageEvent) {
            that._processedWorkerEvents.push(event);
        };

        if (this._pool == null) this._pool = new Pool(8, true, cb);
        this._pool.setAssetGeometryOwnership(this._assetGeometryOwnership);

        const addedCache: { [n: string]: { [n: string]: string; }; } = {};
        const addedStatus: { [n: string]: { [n: string]: number; }; } = {};
        const rootBlocks = [];
        const displayBounds: BABYLON.BoundingBox =
            minDisplayBounds != null && maxDisplayBounds != null
                ? new BABYLON.BoundingBox(
                    new BABYLON.Vector3(minDisplayBounds.X, Number.NEGATIVE_INFINITY, minDisplayBounds.Y),
                    new BABYLON.Vector3(maxDisplayBounds.X, Number.POSITIVE_INFINITY, maxDisplayBounds.Y)
                )
                : null;

        for (const octreeMeta of octrees) {
            const extentInfo = new BABYLON.BoundingBox(
                new BABYLON.Vector3(
                    octreeMeta.Extents.Minimum.X / 1000,
                    octreeMeta.Extents.Minimum.Z / 1000,
                    octreeMeta.Extents.Minimum.Y / 1000
                ),
                new BABYLON.Vector3(
                    octreeMeta.Extents.Maximum.X / 1000,
                    octreeMeta.Extents.Maximum.Z / 1000,
                    octreeMeta.Extents.Maximum.Y / 1000
                )
            );
            const rootBlock = new FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>(
                this._octreeBlocks++,
                extentInfo.minimum,
                extentInfo.maximum,
                100,
                0,
                null
            );

            this.constructOctreeOctants(
                rootBlock,
                octreeMeta,
                addedCache,
                addedStatus,
                displayBounds,
                octreeMeta.assetLayerId
            );
            rootBlocks.push(rootBlock);
        }

        this._displayDataCache = this._displayDataCache || {};
        this._displayDataStatus = this._displayDataStatus || {};
        Object.assign(this._displayDataCache, addedCache);
        Object.assign(this._displayDataStatus, addedStatus);

        return rootBlocks;
    }

    public assignUnmappedAssetPagesToLayer(assetLayerId: string): void {
        if (!assetLayerId || !this._displayDataStatus) return;

        for (const blockId in this._displayDataStatus) {
            for (const pageId in this._displayDataStatus[blockId]) {
                const pageKey = `${blockId}_${pageId}`;
                if (this._assetLayerByNodeKey[pageKey] == null) {
                    this._assetLayerByNodeKey[pageKey] = assetLayerId;
                }
            }
        }
    }

    public setupOctreesDataPreserveContent(
        octrees: {
            Extents: any;
            Children?: any[];
            assetLayerId?: string;
            ontologyId?: string;
        }[],
        minDisplayBounds?: IVector2D,
        maxDisplayBounds?: IVector2D
    ): {} {
        const previousCache = this._displayDataCache || {};
        const previousStatus = this._displayDataStatus || {};
        const previousPageLength = this._pageLength || {};
        const previousAssetLayerByNodeKey = { ...this._assetLayerByNodeKey };

        this._octreeBlocks = 1;
        const rootBlocks = this.setupOctreesData(octrees, minDisplayBounds, maxDisplayBounds) as any;

        for (const blockId in this._displayDataStatus) {
            const pages = this._displayDataStatus[blockId];
            for (const pageId in pages) {
                const nextDisplayListId = this._displayDataCache?.[blockId]?.[pageId];
                const previousDisplayListId = previousCache?.[blockId]?.[pageId];
                const pageKey = `${blockId}_${pageId}`;
                const hasSameSourceAssetSet = this._assetLayerByNodeKey[pageKey]
                    === previousAssetLayerByNodeKey[pageKey];

                if (nextDisplayListId &&
                    nextDisplayListId === previousDisplayListId &&
                    hasSameSourceAssetSet &&
                    previousStatus?.[blockId]?.[pageId] === 2) {
                    pages[pageId] = 2;

                    if (previousPageLength[pageKey] != null) {
                        this._pageLength[pageKey] = previousPageLength[pageKey];
                    }
                }
            }
        }

        // setupOctreesData replaces the page maps. Dispose any previously
        // tracked page that was not preserved, otherwise its meshes and cached
        // memory remain orphaned when an asset-set octree is removed or moves
        // to a different root-block position.
        this._meshManager.beginCylinderInstanceDisposalBatch();
        try {
            for (const blockId in previousStatus) {
                const pages = previousStatus[blockId];
                for (const pageId in pages) {
                    const pageKey = `${blockId}_${pageId}`;
                    const previousDisplayListId = previousCache?.[blockId]?.[pageId];
                    const nextDisplayListId = this._displayDataCache?.[blockId]?.[pageId];
                    const hasSameSourceAssetSet = this._assetLayerByNodeKey[pageKey]
                        === previousAssetLayerByNodeKey[pageKey];
                    const wasPreserved = pages[pageId] === 2
                        && !!nextDisplayListId
                        && nextDisplayListId === previousDisplayListId
                        && hasSameSourceAssetSet;

                    if (wasPreserved) continue;

                    const previousLength = previousPageLength[pageKey] || 0;
                    if (pages[pageId] === 2 || previousLength > 0) {
                        this._disposeTrackedAssetPage(blockId, pageId, previousLength);
                    }
                    else {
                        delete this._filteredAssetIdsByPage[pageKey];
                    }
                }
            }
        }
        finally {
            this._meshManager.endCylinderInstanceDisposalBatch();
        }

        return rootBlocks;
    }

    private convertTile3DToOctreeBlock(
        detailNode: FrustumStructures.Tile3DDetailModel,
        contentMap: Map<string, FrustumStructures.Tile3DContentModel>,
        idCounter: { value: number },
        depth: number,
        capacity: number,
        creationFunc: (entry: FrustumStructures.TileNode, block: FrustumRegionWorker.OctreeBlock<FrustumStructures.TileNode>) => void,
        displayBounds : BABYLON.BoundingBox
    ): FrustumRegionWorker.OctreeBlock<FrustumStructures.TileNode> {

        const min = new BABYLON.Vector3(detailNode.ExtentsMin.X, detailNode.ExtentsMin.Y, detailNode.ExtentsMin.Z);
        const max = new BABYLON.Vector3(detailNode.ExtentsMax.X, detailNode.ExtentsMax.Y, detailNode.ExtentsMax.Z);

            if(displayBounds != null)
            {
                if(!this.boxesIntersectVec2({X:min.x, Y:min.z}, {X:max.x, Y:max.z}, {X: displayBounds.minimum.x, Y: displayBounds.minimum.z}, {X: displayBounds.maximum.x, Y: displayBounds.maximum.z}))
                    return;
            }


        const block = new FrustumRegionWorker.OctreeBlock<FrustumStructures.TileNode>(
            idCounter.value++,
            min,
            max,
            capacity,
            depth,
            creationFunc
        );

        // --- Populate `entries` and `pageExtents` ---
        for (const contentId of detailNode.Content) {
            const contentModel = contentMap.get(contentId);
            if (!contentModel) continue;

            const minX = contentModel.ExtentsMin.X;
            const maxX = contentModel.ExtentsMax.X;
            const minY = contentModel.ExtentsMin.Y; // flipped
            const maxY = contentModel.ExtentsMax.Y;
            const minZ = contentModel.ExtentsMin.Z; // flipped
            const maxZ = contentModel.ExtentsMax.Z;

            if (displayBounds != null) {
                if (!this.boxesIntersectVec2({ X: minX, Y: minZ }, { X: maxX, Y: maxZ }, { X: displayBounds.minimum.x, Y: displayBounds.minimum.z }, { X: displayBounds.maximum.x, Y: displayBounds.maximum.z }))
                    continue;
            }

            const entry: FrustumStructures.TileNode = {
                id: contentModel.ID,
                visibilityState: 0
            };

            block.addEntry(entry); // puts into `entries`



            block.pageExtents.push([
                new BABYLON.Vector3(minX, minY, minZ), // 0
                new BABYLON.Vector3(maxX, maxY, maxZ),  // 7
                new BABYLON.Vector3(maxX, minY, minZ), // 1
                new BABYLON.Vector3(minX, maxY, minZ), // 2
                new BABYLON.Vector3(maxX, maxY, minZ), // 3
                new BABYLON.Vector3(minX, minY, maxZ), // 4
                new BABYLON.Vector3(maxX, minY, maxZ), // 5
                new BABYLON.Vector3(minX, maxY, maxZ)
            ]);
        }

        // --- Populate `childContent` ---
        for (const contentId of detailNode.ChildContent) {
            const contentModel = contentMap.get(contentId);
            if (!contentModel) continue;

            //const childEntry = new FrustumStructures.TileNode();

            var minX = contentModel.ExtentsMin.X;
            var maxX = contentModel.ExtentsMax.X;
            var minY = contentModel.ExtentsMin.Y; // flipped
            var maxY = contentModel.ExtentsMax.Y;
            var minZ = contentModel.ExtentsMin.Z; // flipped
            var maxZ = contentModel.ExtentsMax.Z;
            if (displayBounds != null) {
                if(!this.boxesIntersectVec2({X:minX, Y:minZ}, {X:maxX, Y:maxZ}, {X: displayBounds.minimum.x, Y: displayBounds.minimum.z}, {X: displayBounds.maximum.x, Y: displayBounds.maximum.z}))
                    continue;
            }

            const childEntry: FrustumStructures.TileNode = {
                id: contentModel.ID,
                visibilityState: 0
            };

            block.childContent.push(childEntry);
        }

        // --- Recurse into Children to populate `blocks[]` ---
        for (let i = 0; i < detailNode.Children.length; i++) {
            const child = detailNode.Children[i];
            const childBlock = this.convertTile3DToOctreeBlock(
                child,
                contentMap,
                idCounter,
                depth + 1,
                capacity,
                creationFunc,
                displayBounds
            );

            block.blocks[i] = childBlock;
        }

        return block;
    }

    private stripFunctionsFromBlock<T>(block: FrustumRegionWorker.OctreeBlock<T>): any {

        /*
        if (block.pageExtents && block.pageExtents.length > 0) {
            for (var x = 0; x < block.pageExtents.length; x++) {
                var minExtent = block.pageExtents[x][0];
                var maxExtent = block.pageExtents[x][1];

                // Compute center and size
                const size = maxExtent.subtract(minExtent);
                const center = minExtent.add(size.scale(0.5));

                // Create the box
                const box = BABYLON.MeshBuilder.CreateBox("box", {
                    width: size.x,
                    height: size.y,
                    depth: size.z
                }, (this._meshManager as any)._babScene);

                // Move it to the correct position
                box.position = center;

                // Optional: give it a material to make it visible
                const mat = new BABYLON.StandardMaterial("boxMat", (this._meshManager as any)._babScene);
                mat.diffuseColor = new BABYLON.Color3(0.2, 0.8, 0.4);
                mat.alpha = 0.5;
                box.material = mat;
            }
        }
        */
        const cleanBlock: any = {
            entries: block.entries,
            pageExtents: block.pageExtents,
            childContent: block.childContent,
            id: block.id,
            _depth: block._depth,
            _maxDepth: block._maxDepth,
            _capacity: block._capacity,
            _minPoint: block._minPoint,
            _maxPoint: block._maxPoint,
            _boundingVectors: block._boundingVectors,
            blocks: []
        };

        if (block.blocks && block.blocks.length > 0) {
            for (let i = 0; i < block.blocks.length; i++) {
                const child = block.blocks[i];
                cleanBlock.blocks[i] = child ? this.stripFunctionsFromBlock(child) : null;
            }
        }



        return cleanBlock;
    }

    private buildContentMap(contentDetails: FrustumStructures.Tile3DContentModel[]): Map<string, FrustumStructures.Tile3DContentModel> {
        const map = new Map<string, FrustumStructures.Tile3DContentModel>();
    for (const content of contentDetails) {
        map.set(content.ID, content);
    }
        return map;
    }

    public getContentExtents(regionData: FrustumStructures.Tile3DTreeModel[], displayBounds?: BABYLON.BoundingBox | null) : BABYLON.BoundingInfo | null {

        let globalMin = new BABYLON.Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY);
        let globalMax = new BABYLON.Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
        let hasAcceptedContent = false;

        for (const region of regionData) {
            for (const content of region.ContentDetails) {
                const cMin = content.ExtentsMin;
                const cMax = content.ExtentsMax;

                if (displayBounds != null) {
                    if (!this.boxesIntersectVec2(
                        { X: cMin.X, Y: cMin.Z },
                        { X: cMax.X, Y: cMax.Z },
                        { X: displayBounds.minimum.x, Y: displayBounds.minimum.z },
                        { X: displayBounds.maximum.x, Y: displayBounds.maximum.z })) {
                        continue;
                    }
                }

                hasAcceptedContent = true;

                globalMin.x = Math.min(globalMin.x, cMin.X);
                globalMin.y = Math.min(globalMin.y, cMin.Y);
                globalMin.z = Math.min(globalMin.z, cMin.Z);

                globalMax.x = Math.max(globalMax.x, cMax.X);
                globalMax.y = Math.max(globalMax.y, cMax.Y);
                globalMax.z = Math.max(globalMax.z, cMax.Z);
            }
        }

        if (!hasAcceptedContent)
            return null;

        const boundingBox = new BABYLON.BoundingBox(globalMin, globalMax);
        var info = new BABYLON.BoundingInfo(boundingBox.minimum, boundingBox.maximum);

        return info;
    }



    public setupOctreeRegionData(
        regionData: FrustumStructures.Tile3DTreeModel[],
        lodOptions: {
            // new-style
            Quality?: "Ultra" | "High" | "Medium" | "Low";
            profiles?: Record<"Ultra" | "High" | "Medium" | "Low", { voxelMM: number[]; distanceMaxByLod: number[] }>;
            CoverageScaleByVoxel?: Record<number, number>;
            Region_Megabyte_Memory_Limit?: number;
            Mode?: "ByTargetPx" | "ByFixedDistances"; // kept for compatibility; we use fixed distances

            // legacy (still accepted, but not required)
            SelectedVoxelSizesMM?: number[];
            DistanceMaxByLod?: number[];
            TargetMaxPxByLod?: number[];

            // misc viewer toggles you were already reading
            [k: string]: any;
        },
        minDisplayBounds?: IVector2D,
        maxDisplayBounds?: IVector2D
    ): {} {
        type QualityKey = "Ultra" | "High" | "Medium" | "Low";

        // ---- Fallback profiles if caller didn't provide a JSON or provided a partial one
        const FALLBACK_PROFILES: Record<QualityKey, { voxelMM: number[]; distanceMaxByLod: number[] }> = {
            Ultra: { voxelMM: [2, 5, 10, 50], distanceMaxByLod: [2.5, 12.5, 50, 100] },
            High: { voxelMM: [5, 10, 25, 50, 100], distanceMaxByLod: [2.5, 12.5, 25, 50, 100] },
            Medium: { voxelMM: [10, 25, 50, 100], distanceMaxByLod: [5, 12.5, 25, 100] },
            Low: { voxelMM: [10, 50, 100], distanceMaxByLod: [2.5, 25, 100] },
        };

        const src = (lodOptions as any)?.settings
            ? { ...(lodOptions as any).settings, profiles: (lodOptions as any).profiles ?? (lodOptions as any).settings.profiles }
            : lodOptions;

        // ---- Merge caller options with sensible defaults
        const defaults = {
            Mode: "ByFixedDistances" as "ByFixedDistances" | "ByTargetPx",
            CoverageScale: 1.3,
            Region_Megabyte_Memory_Limit: DisplayManager.MEMORY_REGION_PRESSURE_MEGABYTE_LIMIT,
            Quality: (this._memoryProfile && (["Ultra", "High", "Medium", "Low"] as const).includes(this._memoryProfile as QualityKey))
                ? (this._memoryProfile as QualityKey)
                : ("Low" as QualityKey),
        };

        const opt = { ...defaults, ...(src || {}) };
        const regionMemoryLimit = Number(opt.Region_Megabyte_Memory_Limit);

        if (Number.isFinite(regionMemoryLimit) && regionMemoryLimit > 0) {
            DisplayManager.MEMORY_REGION_PRESSURE_MEGABYTE_LIMIT = regionMemoryLimit;
        }

        const profiles = (opt.QualityConfig.profiles ?? FALLBACK_PROFILES) as Record<QualityKey, { voxelMM: number[]; distanceMaxByLod: number[] }>;

        // Normalize to memory profile if present
        opt.Quality = this._memoryProfile as QualityKey ?? opt.Quality;

        // Helper: does *every* content item have *all* LOD levels required by a profile?
        const profileIsAvailableEverywhere = (quality: QualityKey): boolean => {
            const prof = profiles[quality];
            if (!prof?.voxelMM?.length) return false;

            // Use a plain array (no Set iteration) to avoid downlevel iterator issues
            const wanted: number[] = prof.voxelMM.map(mm => Math.round(Number(mm)));

            for (let r = 0; r < regionData.length; r++) {
                const contents = (regionData[r] as any)?.ContentDetails as any[] | undefined;
                if (!contents) continue;

                for (let c = 0; c < contents.length; c++) {
                    const content = contents[c];
                    if (content?.ContentType === "CAD Data") continue;

                    const lods = (content?.LODs ?? []) as any[];
                    const have = new Set<number>(lods.map(l => Math.round(Number(l?.LODLevel))));

                    // Iterate the array, check membership via the Set
                    for (let i = 0; i < wanted.length; i++) {
                        if (!have.has(wanted[i])) return false; // missing one → profile not satisfied
                    }
                }
            }
            return true;
        };

        // ---- Pick best available quality by stepping down from requested ----
        const QUALITY_ORDER: QualityKey[] = ["Ultra", "High", "Medium", "Low"];
        var startIdx = QUALITY_ORDER.indexOf(opt.Quality as QualityKey);
        let chosenQuality: QualityKey | null = null;

        if (startIdx < 0) {
            if (profileIsAvailableEverywhere(opt.Quality as QualityKey)) {
                chosenQuality = opt.Quality as QualityKey;
            }
            else {
                startIdx = 0;
            }
        }

        if (startIdx >= 0) {
            for (let i = startIdx; i < QUALITY_ORDER.length; i++) {
                const q = QUALITY_ORDER[i] as QualityKey;

                if (profileIsAvailableEverywhere(q)) {
                    chosenQuality = q;
                    break;
                } else {
                    console.warn(`[LOD] Not all content supports "${q}" LODs. Trying next lower profile...`);
                }
            }
        }


        // If none are fully available everywhere, keep the requested one and let later logic skip missing LODs
        if (!chosenQuality) {
            chosenQuality = opt.Quality as QualityKey;
            console.warn(`[LOD] No profile is fully available everywhere. Proceeding with requested "${chosenQuality}" and skipping missing LODs as needed.`);
        }

        // Persist the chosen quality for subsequent runs
        this._memoryProfile = chosenQuality;
        this._targettedMemoryProfile = chosenQuality;
        const selectedMeshProfile = this.normalizeMeshMemoryProfile(this._meshMemoryProfile) ?? this.mapPointProfileToMeshProfile(chosenQuality);
        this._meshMemoryProfile = selectedMeshProfile;
        this._targettedMeshMemoryProfile = this._targettedMeshMemoryProfile || selectedMeshProfile;
        this._meshLodDistances = DisplayManager.FALLBACK_MESH_PROFILES[selectedMeshProfile].distanceMaxByLod.slice();
        this._meshRequestedLodLevels = DisplayManager.FALLBACK_MESH_PROFILES[selectedMeshProfile].requestedLodLevels.slice();

        // ---- Build selected voxel sizes and distances from chosen profile (or fallback) ----
        let selectedVoxelSizesMM: number[] = [];
        let distanceMaxByLod: number[] = [];

        const prof = profiles[chosenQuality];
        if (prof && prof.voxelMM?.length && prof.voxelMM.length === prof.distanceMaxByLod?.length) {
            const zipped = prof.voxelMM
                .map((mm, i) => ({ mm: Math.round(Number(mm)), dist: Number(prof.distanceMaxByLod[i]!) }))
                .sort((a, b) => a.mm - b.mm);
            selectedVoxelSizesMM = zipped.map(z => z.mm);
            distanceMaxByLod = zipped.map(z => z.dist);
        } else {
            // final safety: use built-in Low
            const p = FALLBACK_PROFILES.Low;
            const zipped = p.voxelMM
                .map((mm, i) => ({ mm: Math.round(Number(mm)), dist: Number(p.distanceMaxByLod[i]!) }))
                .sort((a, b) => a.mm - b.mm);
            selectedVoxelSizesMM = zipped.map(z => z.mm);
            distanceMaxByLod = zipped.map(z => z.dist);
            console.warn('[LOD] Invalid or mismatched profile arrays; defaulting to fallback "Low".');

            this._memoryProfile = "Low";
        }

        // ---- Per-voxel coverage scale wiring ----
        const lut: Record<number, number> = {};
        const byVoxel = (opt as any).CoverageScaleByVoxel;
        const defaultScale = Number(opt.CoverageScale ?? 1.3);

        if (byVoxel && typeof byVoxel === "object") {
            for (const [k, v] of Object.entries(byVoxel)) {
                const mm = Math.round(Number(k));
                const s = Number(v);
                if (Number.isFinite(mm) && Number.isFinite(s)) lut[mm] = s;
            }
        }

        {
            const matMgr = this._meshManager.getMaterialManager();
            if (matMgr && typeof (matMgr as any).setSplatCoverageScaleByVoxel === "function" && Object.keys(lut).length) {
                (matMgr as any).setSplatCoverageScaleByVoxel(lut, defaultScale);
            } else if (matMgr && typeof (matMgr as any).setSplatCoverageScale === "function") {
                (matMgr as any).setSplatCoverageScale(defaultScale);
            }
        }

        // Initialize mapping and distances
        this._initLodMappings(selectedVoxelSizesMM);
        this.lodDistances = distanceMaxByLod.slice();

        // ---------- Worker/caches init ----------
        const cb = (workerNumber: number, event: MessageEvent) => {
            this._processedWorkerEvents.push(event);
        };
        const numThreads = 8;
        if (this._pool == null) this._pool = new Pool(numThreads, true, cb);

        const displayBounds: BABYLON.BoundingBox | null = (minDisplayBounds && maxDisplayBounds)
            ? new BABYLON.BoundingBox(
                new BABYLON.Vector3(minDisplayBounds.X, Number.NEGATIVE_INFINITY, minDisplayBounds.Y),
                new BABYLON.Vector3(maxDisplayBounds.X, Number.POSITIVE_INFINITY, maxDisplayBounds.Y)
            )
            : null;

        const contentExtents = this.getContentExtents(regionData, displayBounds);
        const idCounter = { value: 0 };
        const blockCapacity = 64;
        const creationFunc = (entry: FrustumStructures.TileNode, block: FrustumRegionWorker.OctreeBlock<FrustumStructures.TileNode>) => block.addEntry(entry);

        const blocks: any[] = [];
        const dataLengthCache: { [contentId: string]: { [lodIndex: number]: number } } = {};
        const displayDataCache: { [contentId: string]: { [lodIndex: number]: string } } = {};
        const displayMetadataCache: { [contentId: string]: { [lodIndex: number]: string } } = {};
        const displayDataTypes: { [contentId: string]: { [lodIndex: number]: string } } = {};
        const displayDataStatus: { [contentId: string]: { [lodIndex: number]: number } } = {};
        const displayDataTargetStatus: { [contentId: string]: number } = {};

        let totalMemoryForRegionDisplay = 0;

        // ---------- Build octree + caches per region ----------
        for (let d = 0; d < regionData.length; d++) {
            const contentMap = this.buildContentMap(regionData[d].ContentDetails);
            const rawBlock = this.convertTile3DToOctreeBlock(
                regionData[d].Root,
                contentMap,
                idCounter,
                0,
                blockCapacity,
                creationFunc,
                displayBounds!
            );

            if (rawBlock == null)
                continue;

            for (const content of regionData[d].ContentDetails) {
                this._regionFileNames[content.ID] = content.Name;
                const contentId = content.ID;

                dataLengthCache[contentId] = {};
                displayDataStatus[contentId] = {};
                displayDataCache[contentId] = {};
                displayMetadataCache[contentId] = {};
                displayDataTypes[contentId] = {};
                displayDataTargetStatus[contentId] = 0;

                let totalMemIncluded = 0;
                let included = 0;

                for (const lodData of content.LODs) {
                    // LODLevel is the VOXEL SIZE (mm)
                    const voxelMM = Math.round(Number(lodData.LODLevel));
                    var idx = (content.ContentType != "CAD Data" && content.ContentType != "Mesh Data") ? this._indexForVoxelMM(voxelMM) : lodData.LODLevel;

                    if (!idx && content.ContentType == "Mesh Data") idx = 1;
                    if (!idx) continue; // not in selected set

                    const memory = this.estimateGeometryMemory({
                        vertexCount: lodData.VertexCount,
                        faceCount: lodData.FaceCount,
                        colorType: "u8",
                        includeNormals: false,
                        use32BitIndices: lodData.VertexCount > 65535 && lodData.FaceCount > 0,
                        keepAppArrays: false,
                        babylonCached: true,
                    });

                    totalMemIncluded += memory.cpu.MB;
                    included++;
                    totalMemoryForRegionDisplay += memory.cpu.MB;

                    dataLengthCache[contentId][idx] = memory.cpu.MB;

                    // Cache file/type (if multiple files per level, adapt as needed)
                    for (let f = 0; f < lodData.FileIDs.length; f++) {
                        displayDataStatus[contentId][idx] = 0;
                        displayDataCache[contentId][idx] = lodData.FileIDs[f];
                        displayDataTypes[contentId][idx] = lodData.DataType;
                    }

                    if (lodData.MetadataFileID != null) {
                        displayMetadataCache[contentId][idx] = lodData.MetadataFileID;
                    }
                }

                if (included > 0) totalMemoryForRegionDisplay += (totalMemIncluded / included);
            }

            blocks.push(this.stripFunctionsFromBlock(rawBlock));
        }

        this._displayRegionDataCache = displayDataCache;
        this._displayRegionMetadataDataCache = displayMetadataCache;
        this._displayRegionDataStatus = displayDataStatus;
        this._displayRegionDataProcessedStatus = displayDataTargetStatus;
        this._regionDataLengths = dataLengthCache;
        this._displayRegionDataTypes = displayDataTypes;

        return { roots: blocks, contentExtents };
    }

    public setRegionMesh(node: any) {
        var id = node.id;
        var state = node.visibilityState;

        var boxMesh = this._boxMeshes[id];
        var pointMesh = this._pointMeshes[id];
        var regionMesh = this._regionMeshes[id];

        /*
        if (boxMesh != null) {
            if (state == 3) {

                boxMesh.setEnabled(false);
                pointMesh.setEnabled(false);
                regionMesh.setEnabled(true);
            }
            else if (state == 2) {
                boxMesh.setEnabled(false);
                pointMesh.setEnabled(true);
                regionMesh.setEnabled(false);
            }
            else {
                boxMesh.setEnabled(true);
                pointMesh.setEnabled(false);
                regionMesh.setEnabled(false);
            }
        }
        */
    }

    // Helpers to map between voxel size (mm) and sequential LOD index
    private _initLodMappings(selectedVoxelSizesMM: number[]) {
        const uniq = Array.from(new Set(selectedVoxelSizesMM.map(v => Number(v))))
            .filter(v => Number.isFinite(v) && v > 0)
            .sort((a, b) => a - b); // 1 = highest detail (smallest voxel size)

        this._voxelMMByIndex = [Number.NaN, ...uniq];
        this._lodIndexByVoxelMM.clear();
        for (let i = 1; i < this._voxelMMByIndex.length; i++) {
            this._lodIndexByVoxelMM.set(this._voxelMMByIndex[i], i);
        }
        this.regionLODLevels = uniq.length;
    }

    private _indexForVoxelMM(vmm: number): number | undefined {
        // Use rounded key to be tolerant of 4.999 -> 5.0 type sources
        return this._lodIndexByVoxelMM.get(Math.round(Number(vmm)));
    }

    private _voxelMMForIndex(idx: number): number | undefined {
        return this._voxelMMByIndex[idx];
    }

    private normalizeMeshMemoryProfile(profile: string | null | undefined): MeshQualityKey | null {
        switch ((profile ?? "").trim().toLowerCase()) {
            case "high":
                return "High";
            case "medium":
                return "Medium";
            case "low":
                return "Low";
            default:
                return null;
        }
    }

    private mapPointProfileToMeshProfile(profile: string | null | undefined): MeshQualityKey {
        switch ((profile ?? "").trim().toLowerCase()) {
            case "low":
                return "Low";
            case "medium":
                return "Medium";
            case "ultra":
            case "high":
            default:
                return "High";
        }
    }

    private getPrimaryRegionLoadType(nodeId: string): string {
        const dataTypes = this._displayRegionDataTypes?.[nodeId];
        if (dataTypes == null) {
            return "";
        }

        const levels = Object.keys(dataTypes)
            .map(level => Number(level))
            .filter(level => Number.isFinite(level))
            .sort((a, b) => a - b);

        for (const level of levels) {
            const type = dataTypes[level]?.toLowerCase?.() ?? "";
            if (type) {
                return type;
            }
        }

        return "";
    }

    private isMeshLikeRegionNode(nodeId: string): boolean {
        const type = this.getPrimaryRegionLoadType(nodeId);
        return type === "mesh" || type === "glb" || type === "pagedglb" || type === "pagedfbx";
    }

    private getLodDistancesForNode(nodeId: string): number[] {
        if (this.isMeshLikeRegionNode(nodeId) && this._meshLodDistances.length > 0) {
            return this._meshLodDistances;
        }

        return this.lodDistances;
    }

    private getRequestedLodLevelsForNode(nodeId: string): number[] {
        if (this.isMeshLikeRegionNode(nodeId) && this._meshRequestedLodLevels.length > 0) {
            return this._meshRequestedLodLevels;
        }

        const distances = this.getLodDistancesForNode(nodeId);
        return Array.from({ length: distances.length }, (_, index) => index + 1);
    }

    private getConfiguredLodLevelCount(nodeId: string): number {
        const requestedLevels = this.getRequestedLodLevelsForNode(nodeId);
        return requestedLevels.length > 0 ? Math.max(...requestedLevels) : 0;
    }

    private getDesiredLodLevel(node: FrustumStructures.TreeNode): number {
        const distances = this.getLodDistancesForNode(node.id);
        const requestedLevels = this.getRequestedLodLevelsForNode(node.id);
        if (distances.length === 0) {
            return -1;
        }

        const projectedSize = Number((node as any).octantProjectedSize);
        if (!Number.isFinite(projectedSize)) {
            const fallbackLodLevel = Number((node as any).lodLevel);
            if (Number.isFinite(fallbackLodLevel)) {
                const roundedLevel = Math.round(fallbackLodLevel);
                if (requestedLevels.includes(roundedLevel)) {
                    return roundedLevel;
                }

                for (let x = 0; x < requestedLevels.length; x++) {
                    if (requestedLevels[x] >= roundedLevel) {
                        return requestedLevels[x];
                    }
                }
            }

            return requestedLevels[requestedLevels.length - 1] ?? -1;
        }

        let lodLevel = requestedLevels[requestedLevels.length - 1] ?? -1;
        for (let x = 0; x < distances.length; x++) {
            if (projectedSize < distances[x]) {
                lodLevel = requestedLevels[x] ?? lodLevel;
                break;
            }
        }

        return lodLevel;
    }

    private getNextAllowedLodLevel(nodeId: string, currentLodLevel: number): number {
        const requestedLevels = this.getRequestedLodLevelsForNode(nodeId);
        for (let x = 0; x < requestedLevels.length; x++) {
            if (requestedLevels[x] > currentLodLevel) {
                return requestedLevels[x];
            }
        }

        return -1;
    }

    private estimateGeometryMemory({
        vertexCount,
        faceCount = 0,            // 0 for point clouds
        colorType = "u8",         // "u8" for UNSIGNED_BYTE, "f32" for Float32 colors
        includeNormals = false,   // normals uploaded?
        use32BitIndices = false,  // 32-bit index buffer?
        keepAppArrays = false,    // you kept your own typed arrays after upload?
        babylonCached = true      // you did NOT call geometry.clearCachedData() ?
    }: {
        vertexCount: number;
        faceCount?: number;
        colorType?: ColorType;
        includeNormals?: boolean;
        use32BitIndices?: boolean;
        keepAppArrays?: boolean;
        babylonCached?: boolean;
    }) {
        const B_F32 = 4;

        const posBytes = vertexCount * 3 * B_F32;                  // 12 B/vertex
        const colBytes = colorType === "u8" ? vertexCount * 4      // 4 B/vertex
            : vertexCount * 4 * B_F32; // 16 B/vertex
        const norBytes = includeNormals ? vertexCount * 3 * B_F32 : 0;

        const indexSize = faceCount > 0 ? (use32BitIndices ? 4 : 2) : 0;
        const idxBytes = faceCount * 3 * indexSize;               // 3 indices/face

        const oneCopy = posBytes + colBytes + norBytes + idxBytes;

        // GPU bytes = one copy (the buffers in VRAM)
        const gpuBytes = oneCopy;

        // CPU bytes = your app arrays (if kept) + Babylon’s cached copies (if not cleared)
        const cpuBytes = (keepAppArrays ? oneCopy : 0) + (babylonCached ? oneCopy : 0);

        const toMiB = (b: number) => b / (1024 * 1024);
        const toMB = (b: number) => b / 1_000_000;

        return {
            cpu: { bytes: cpuBytes, MiB: toMiB(cpuBytes), MB: toMB(cpuBytes) },
            gpu: { bytes: gpuBytes, MiB: toMiB(gpuBytes), MB: toMB(gpuBytes) },
            // “total” is just cpu+gpu for reference
            total: { bytes: cpuBytes + gpuBytes, MiB: toMiB(cpuBytes + gpuBytes), MB: toMB(cpuBytes + gpuBytes) }
        };
    }

    public setDataRequestCallback(dataRequestCallback: any): void {
        DisplayWorker.DataRequestCallback = dataRequestCallback;
    }

    private constructOctreeOctants(
        octant: FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>,
        octree: any,
        cache: { [n: number]: { [n: number]: string; }; },
        status: { [n: number]: { [n: number]: number; }; },
        displayBounds?: BABYLON.BoundingBox,
        assetSetID?: string
    ) {
        if (DebugOptions.valueOf() && octree.OctantContent.length > 0) {
            this._meshManager.createOctantBBox(octant.minPoint, octant.maxPoint, octant.id, octree.OctantContent.length);
        }

        if (cache[octant.id] != null || status[octant.id] != null) {
            console.log("Error in initialization!");
        }

        cache[octant.id] = {};
        status[octant.id] = {};

        for (var i = 0; i < octree.OctantContent.length; i++) {

            this._numContent++;
            var octantContent = octree.OctantContent[i];

            var minPoint = new BABYLON.Vector3(octantContent.Extents.Minimum.X / 1000, octantContent.Extents.Minimum.Z / 1000, octantContent.Extents.Minimum.Y / 1000);
            var maxPoint = new BABYLON.Vector3(octantContent.Extents.Maximum.X / 1000, octantContent.Extents.Maximum.Z / 1000, octantContent.Extents.Maximum.Y / 1000);

            if (displayBounds != null) {
                if (!this.boxesIntersectVec2({ X: minPoint.x, Y: minPoint.z }, { X: maxPoint.x, Y: maxPoint.z }, { X: displayBounds.minimum.x, Y: displayBounds.minimum.z }, { X: displayBounds.maximum.x, Y: displayBounds.maximum.z }))
                    continue;
            }

            var extents: BABYLON.Vector3[] = [];
            extents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            extents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));

            extents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));

            extents[2].x = maxPoint.x;

            extents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            extents[3].y = maxPoint.y;

            extents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            extents[4].z = maxPoint.z;

            extents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            extents[5].z = minPoint.z;

            extents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            extents[6].x = minPoint.x;

            extents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            extents[7].y = minPoint.y;

            minPoint = new BABYLON.Vector3(octantContent.RefObjectExtents.Minimum.X / 1000, octantContent.RefObjectExtents.Minimum.Z / 1000, octantContent.RefObjectExtents.Minimum.Y / 1000);
            maxPoint = new BABYLON.Vector3(octantContent.RefObjectExtents.Maximum.X / 1000, octantContent.RefObjectExtents.Maximum.Z / 1000, octantContent.RefObjectExtents.Maximum.Y / 1000);



            var largestObjectExtents: BABYLON.Vector3[] = [];

            largestObjectExtents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            largestObjectExtents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));

            largestObjectExtents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));

            largestObjectExtents[2].x = maxPoint.x;

            largestObjectExtents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            largestObjectExtents[3].y = maxPoint.y;

            largestObjectExtents.push(new BABYLON.Vector3(minPoint.x, minPoint.y, minPoint.z));
            largestObjectExtents[4].z = maxPoint.z;

            largestObjectExtents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            largestObjectExtents[5].z = minPoint.z;

            largestObjectExtents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            largestObjectExtents[6].x = minPoint.x;

            largestObjectExtents.push(new BABYLON.Vector3(maxPoint.x, maxPoint.y, maxPoint.z));
            largestObjectExtents[7].y = minPoint.y;

            var tNode: FrustumStructures.TreeNode = {
                id: octantContent.PageID.toString(),
                blockId: octant.id.toString(),
                largestEntryVolume: 0,
                octantDistanceToCamera: 0,
                octantProjectedSize: 0,
                renderPriority: 0,
                visible: true
            };

            this.NumNodes++;

            octant.entries.push(tNode);
            octant.pageExtents.push(extents);
            octant.pageLargestObjectExtents.push(largestObjectExtents);

            cache[octant.id][octantContent.PageID] = octantContent.DisplayListID;
            status[octant.id][octantContent.PageID] = 0;

            const nodeKey = `${octant.id}_${tNode.id}`;

            if(assetSetID)
                this._assetLayerByNodeKey[nodeKey] = assetSetID;
        }

        if (octree.ChildOctants) {
            for (var x = 0; x < octree.ChildOctants.length; x++) {

            if (octree.ChildOctants[x] != null) {

                var octantInfo = octree.ChildOctants[x];

                var minPoint = new BABYLON.Vector3(octantInfo.Extents.Minimum.X / 1000, octantInfo.Extents.Minimum.Z / 1000, octantInfo.Extents.Minimum.Y / 1000);
                var maxPoint = new BABYLON.Vector3(octantInfo.Extents.Maximum.X / 1000, octantInfo.Extents.Maximum.Z / 1000, octantInfo.Extents.Maximum.Y / 1000)

                if (displayBounds != null) {
                    var octantBounds = new BABYLON.BoundingBox(minPoint, maxPoint);

                    if (!this.boxesIntersectVec2({ X: minPoint.x, Y: minPoint.z }, { X: maxPoint.x, Y: maxPoint.z }, { X: displayBounds.minimum.x, Y: displayBounds.minimum.z }, { X: displayBounds.maximum.x, Y: displayBounds.maximum.z }))
                        continue;
                }

                var childOctant = new FrustumMeshWorker.OctreeBlock<FrustumStructures.TreeNode>(this._octreeBlocks, minPoint, maxPoint, 100, octant._depth + 1, null);
                this._octreeBlocks++;
                octant.blocks[x] = childOctant;

                // CHANGED: pass assetLayerId down so children’s nodes also get mapped
                this.constructOctreeOctants(childOctant, octree.ChildOctants[x], cache, status, displayBounds, assetSetID);
            }
        }
    }
}


    private boxesIntersectVec2(aMin:IVector2D, aMax:IVector2D, bMin:IVector2D, bMax:IVector2D, countEdgeTouch = true) {

          if (countEdgeTouch) {
            // inclusive overlap (edges count)
            return aMin.X <= bMax.X && bMin.X <= aMax.X &&   // X‑ranges overlap
                   aMin.Y <= bMax.Y && bMin.Y <= aMax.Y;     // Y‑ranges overlap
          }
          // strict overlap (edges must *overlap*, not just touch)
          return aMin.X <  bMax.X && bMin.X <  aMax.X &&
                 aMin.Y <  bMax.Y && bMin.Y <  aMax.Y;
        }
    public updateDisplayDataExtents(boundingInfo: any, currentExtentInfo: BABYLON.BoundingBox) {

        if (currentExtentInfo.minimum.x > boundingInfo.Minimum.X / 1000) {
            currentExtentInfo.minimum.x = boundingInfo.Minimum.X / 1000;
        }
        if (currentExtentInfo.minimum.y > boundingInfo.Minimum.Y / 1000) {
            currentExtentInfo.minimum.y = boundingInfo.Minimum.Y / 1000;
        }
        if (currentExtentInfo.minimum.z > boundingInfo.Minimum.Z / 1000) {
            currentExtentInfo.minimum.z = boundingInfo.Minimum.Z / 1000;
        }

        if (currentExtentInfo.maximum.x < boundingInfo.Maximum.X / 1000) {
            currentExtentInfo.maximum.x = boundingInfo.Maximum.X / 1000;
        }
        if (currentExtentInfo.maximum.y < boundingInfo.Maximum.Y / 1000) {
            currentExtentInfo.maximum.y = boundingInfo.Maximum.Y / 1000;
        }
        if (currentExtentInfo.maximum.z < boundingInfo.Maximum.Z / 1000) {
            currentExtentInfo.maximum.z = boundingInfo.Maximum.Z / 1000;

        }
    }

    public processMemoryPressureDisplayExperimental(displayManager: DisplayManager, visibleNodes: FrustumStructures.TreeNode[], hiddenNodes: FrustumStructures.TreeNode[]) {

        var freeableMemroy = false;

        displayManager._visibleNodes = visibleNodes;
        displayManager._hiddenNodes = hiddenNodes;

        // attempt to free up memory!
        if (displayManager._dataLength >= displayManager.getEffectiveAssetMemoryLimit()) {
            var allHiddenUnloaded = true;
            if (hiddenNodes.length > 0) {

                for (var z = hiddenNodes.length - 1; z >= 0; z--) {
                    var node: FrustumStructures.TreeNode = hiddenNodes[z];

                    if ((displayManager._displayDataStatus[node.blockId][node.id] == 2)) {
                        freeableMemroy = true;
                        break;
                    }
                }
            }

            if (!freeableMemroy) {
                // hit memory pressure but no more stuff remains outside frustum. Therefore, we will check if there is a higher priority UNLOADED page and a lower priority LOADED page. In this case,
                // we dispose the lower priorty page.

                var loadedLowPrioIndex = -1;
                var unloadedHighPrioIndex = -1;

                for (var x = visibleNodes.length - 1; x >= 0; x--) {

                    var node: FrustumStructures.TreeNode = visibleNodes[x];

                    if (displayManager._displayDataStatus[node.blockId][node.id] == 2) {

                        loadedLowPrioIndex = x;
                        break;
                    }
                }

                for (var x = 0; (x <= visibleNodes.length - 1) && (x < loadedLowPrioIndex); x++) {

                    var node: FrustumStructures.TreeNode = visibleNodes[x];

                    if (node.octantProjectedSize >= 5 && displayManager._displayDataStatus[node.blockId][node.id] == 0) {

                        unloadedHighPrioIndex = x;
                        break;
                    }
                }

                if (unloadedHighPrioIndex != -1 && loadedLowPrioIndex != -1) {

                    freeableMemroy = true;
                }
            }
        }

        var totalCompleteInFrustum = 0;

        if (DebugOptions.valueOf()) {
            this._meshManager.clearBoxPageCounts();
        }

        for (var x = 0; x <= visibleNodes.length - 1; x++) {


            var node: FrustumStructures.TreeNode = visibleNodes[x];
            var nodeStatus = displayManager._displayDataStatus[node.blockId][node.id];

            if (DebugOptions.valueOf()) {
                this._meshManager.setBoxPageVisible(node.blockId, 1);
            }

            if (node.octantProjectedSize < 5) {
                // If the projected size is less than 5 pixels, then do not load it. Note we do not UNLOAD pages that become less than 5 pixels (unless memory pressure comes into play).
                totalCompleteInFrustum++;
            }
            else if (nodeStatus == 0) {

                if ((displayManager._pool == null || displayManager._pool._availableWorkers.length > 0) && ((displayManager._dataLength < displayManager.getEffectiveAssetMemoryLimit()) || freeableMemroy)) {
                    displayManager.queueDisplayOctantBatch(visibleNodes, x);
                }
            }
            else if (nodeStatus == 2) {
                totalCompleteInFrustum++;
            }
        }

        if (visibleNodes.length == 0) {
            displayManager._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYFINISHED, 100);
        }
        else if ((displayManager._dataLength >= displayManager.getEffectiveAssetMemoryLimit()) && !freeableMemroy) {

            //displayManager._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYMEMORYLIMIT, (totalCompleteInFrustum / visibleNodes.length) * 100);
            displayManager._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYFINISHED, 100);
        }
        else {
            displayManager._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYFINISHED, (totalCompleteInFrustum / visibleNodes.length) * 100);
        }
    }

    public processRegionMemoryPressureDisplay(displayManager: DisplayManager, visibleNodes: FrustumStructures.TreeNode[], hiddenNodes: FrustumStructures.TreeNode[]) {

        displayManager._regionVisibleNodes = visibleNodes ?? [];
        visibleNodes = visibleNodes ?? [];
        hiddenNodes = hiddenNodes ?? [];

        const allowMesh = displayManager._canLoadCadRegions();
        const allowPC = displayManager._contentLayers.pointCloud;
        const allowSmartMesh = displayManager._contentLayers.mesh;
        displayManager._visibleRegionNodes = displayManager._copyNodeArray(visibleNodes);
        displayManager._hiddenRegionNodes = displayManager._copyNodeArray(hiddenNodes);

        if (!allowMesh && !allowPC && !allowSmartMesh) {
            displayManager.setRegionMemoryPressureHit(false);
            displayManager.fireCombinedDisplayProgress();
            return;
        }
        var sorted = visibleNodes;

        displayManager.ensureRegionBudgetWithoutLODChange(sorted);

        var budgetPlan = displayManager.planRegionDisplayBudget(sorted, hiddenNodes);
        var allowedVisibleNodeIds = budgetPlan.allowedVisibleNodeIds;
        var memroyPressureHit = budgetPlan.memoryPressureHit;
        displayManager.setRegionMemoryPressureHit(memroyPressureHit);

        if (displayManager._pool._availableWorkers.length == 0)
        {
            displayManager.fireCombinedDisplayProgress();
            return;
        }

        var numMeshes = 0;
        var numPointClouds = 0;

        for (var x = 0; x < sorted.length; x++) {

            if (displayManager._pool._availableWorkers.length == 0)
                break;


            var nodeToDisplay: FrustumStructures.TreeNode = sorted[x];

            if (nodeToDisplay == null)
                continue;

            if (!allowedVisibleNodeIds.has(nodeToDisplay.id))
                continue;

            var nodeStatus = displayManager._displayRegionDataStatus[nodeToDisplay.id][0];

            var targetLodLevel = displayManager.getClosestLodLevel(nodeToDisplay);

            if (targetLodLevel <= 0)
                continue;

            var targetNodeStatus = targetLodLevel * 2;

            var type = displayManager._displayRegionDataTypes[nodeToDisplay.id]?.[targetNodeStatus / 2]?.toLowerCase?.() ?? "";

            if (type == "")
                continue;

            if ((type === "mesh") && !allowSmartMesh) continue;
            if ((type === "glb" || type === "pagedglb" || type === "pagedfbx") && !allowMesh) continue;
            if ((type === "point cloud" || type === "pointcloud") && !allowPC) continue;

            if (nodeStatus != targetNodeStatus
                && displayManager._displayRegionDataProcessedStatus[nodeToDisplay.id] == targetNodeStatus) {
                displayManager._displayRegionDataStatus[nodeToDisplay.id][0] = targetNodeStatus;
                continue;
            }

            var regionName = displayManager._regionFileNames[Number(nodeToDisplay.id)];

            if (nodeStatus != targetNodeStatus) {

                if (nodeStatus != targetNodeStatus - 1
                    && (type === "mesh" || type === "pagedglb" || type === "pagedfbx" || type === "point cloud" || type === "pointcloud")
                    && displayManager.queueDisplayRegionOctantBatch(sorted, x, allowedVisibleNodeIds)) {
                    continue;
                }

                if (displayManager.showRegionBoxes)
                    displayManager._regionMeshes[Number(nodeToDisplay.id)].setEnabled(true);

                // This is old code for displaying meshes with multiple segments. This is untested with latest edition of tiles, and
                // should only be enabled if we wish to have meshes displayed with multiple parts.
                /*
                if (displayManager._displayRegionDataTypes[nodeToDisplay.id][targetNodeStatus / 2].toLowerCase() == "mesh") {

                    if ((displayManager._pool == null || displayManager._pool._availableWorkers.length > 0)) {

                        if (displayManager._displayRegionDataStatus[nodeToDisplay.id][0] == 0) {
                            displayManager._displayRegionDataStatus[nodeToDisplay.id][0] = targetNodeStatus - 1;

                            displayManager._regionDataLength += displayManager.getRegionMemory(nodeToDisplay.id, targetNodeStatus / 2);
                        }

                    // Region is not displayed as mesh, or not all segments are displayed...
                        for (var s = 0; s < displayManager._regionSegmentCount[nodeToDisplay.id]; s++) {

                            var segmentStatus = displayManager._displayRegionDataStatus[nodeToDisplay.id][s + 1];

                            if (segmentStatus == 0) {

                                displayManager._displayRegionDataStatus[nodeToDisplay.id][s + 1] = targetNodeStatus - 1;

                                // To Do, for meshes, this should be a collection
                                var fileID = displayManager._displayRegionDataCache[nodeToDisplay.id][targetNodeStatus/2];
                                //numMeshes++;
                                displayManager.processDisplayRegionOctantContent(fileID,
                                    nodeToDisplay.id,
                                    (s + 1).toString(),
                                    x,
                                    targetNodeStatus,
                                    displayManager._displayRegionDataTypes[nodeToDisplay.id][targetNodeStatus/2]);

                                break;
                            }
                        }
                    }
                }
                */
                {
                    if (nodeStatus != targetNodeStatus - 1) {

                        if ((displayManager._pool == null || displayManager._pool._availableWorkers.length > 0)) {
                            var memoryRequired = displayManager.getRegionMemory(nodeToDisplay.id, targetNodeStatus / 2);

                            if (displayManager.showRegionBoxes)
                                displayManager._pointMeshes[Number(nodeToDisplay.id)].setEnabled(true);

                            displayManager._regionDataLength += memoryRequired;
                            displayManager._displayRegionDataStatus[nodeToDisplay.id][0] = targetNodeStatus - 1;

                            // To Do, for meshes, this should be a collection.
                            var fileID = displayManager._displayRegionDataCache[nodeToDisplay.id][targetNodeStatus/2];

                            displayManager.processDisplayRegionOctantContent(fileID, nodeToDisplay.id, "0", x, targetNodeStatus, displayManager._displayRegionDataTypes[nodeToDisplay.id][targetNodeStatus/2]);
                        }
                    }
                }
            }
        }

        displayManager.fireCombinedDisplayProgress();
    }

    getClosestLodLevel(node: FrustumStructures.TreeNode) : number {

        var lodLevel = this.getDesiredLodLevel(node);


        if (this._regionDataLengths == null || this._regionDataLengths[node.id] == null) {

            // This has only been seen to happen when you kill a coordinator (from changing range view perhaps),
            // and the coordinator still gasps back a callback to load its content. In this case
            // just ignore any nodes that get asked to be loaded for which we do not have a display status.
            return -1;
        }

        var maxLodLevel = Math.max(this.getConfiguredLodLevelCount(node.id), this.getMaxAvailableRegionLodLevel(node.id));

        for (var coarserLevel = lodLevel; coarserLevel <= maxLodLevel; coarserLevel++) {
            if (this._regionDataLengths[node.id][coarserLevel] != null)
                return coarserLevel;
        }

        for (var denserLevel = lodLevel - 1; denserLevel > 0; denserLevel--) {
            if (this._regionDataLengths[node.id][denserLevel] != null)
                return denserLevel;
        }

        console.error("Unable to find LOD level for content");
        return -1;
    }
    getNextLowestLODLevel(node: FrustumStructures.TreeNode): number {

        var lodLevel = this.getNextAllowedLodLevel(node.id, Number((node as any).lodLevel));
        if (lodLevel < 0) {
            return -1;
        }
        var maxLodLevel = Math.max(this.getConfiguredLodLevelCount(node.id), this.getMaxAvailableRegionLodLevel(node.id));

        for (var searchingLevel = lodLevel; searchingLevel <= maxLodLevel; searchingLevel++) {
            if (this._regionDataLengths[node.id][searchingLevel] != null)
                return searchingLevel;
        }

        return -1;
    }

    private getMaxAvailableRegionLodLevel(blockId: string): number {
        var dataLengths = this._regionDataLengths?.[blockId];
        if (dataLengths == null) {
            return 0;
        }

        return Object.keys(dataLengths)
            .map(level => Number(level))
            .filter(level => Number.isFinite(level) && level > 0 && dataLengths[level] != null)
            .reduce((maxLevel, level) => Math.max(maxLevel, level), 0);
    }

    ensureRegionBudgetWithoutLODChange(nodes: FrustumStructures.TreeNode[]) {
        for (var x = 0; x < nodes.length; x++) {
            var node = nodes[x];
            (node as any).lodLevel = this.getClosestLodLevel(node);
        }
    }

    private planRegionDisplayBudget(visibleNodes: FrustumStructures.TreeNode[], hiddenNodes: FrustumStructures.TreeNode[]): { allowedVisibleNodeIds: Set<string>; memoryPressureHit: boolean; } {
        visibleNodes = visibleNodes ?? [];
        hiddenNodes = hiddenNodes ?? [];

        const allowedVisibleNodeIds = new Set<string>();
        const plannedVisibleNodes: { node: FrustumStructures.TreeNode; memoryRequired: number; }[] = [];
        const regionMemoryLimit = this.getEffectiveRegionMemoryLimit();
        let totalRequiredMemory = 0;

        for (var x = 0; x < visibleNodes.length; x++) {
            var visibleNode = visibleNodes[x];
            var targetLodLevel = this.getClosestLodLevel(visibleNode);

            (visibleNode as any).lodLevel = targetLodLevel;

            if (targetLodLevel <= 0) {
                continue;
            }

            var targetNodeStatus = targetLodLevel * 2;

            if (!this.canDisplayRegionTargetStatus(visibleNode.id, targetNodeStatus)) {
                continue;
            }

            var memoryRequired = this.getRegionMemory(visibleNode.id, targetLodLevel);

            plannedVisibleNodes.push({
                node: visibleNode,
                memoryRequired: memoryRequired
            });
            allowedVisibleNodeIds.add(visibleNode.id);
            totalRequiredMemory += memoryRequired;
        }

        for (var z = hiddenNodes.length - 1; z >= 0; z--) {
            var hiddenNode = hiddenNodes[z];
            var hiddenNodeStatus = this._displayRegionDataStatus?.[hiddenNode.id]?.[0] ?? 0;

            if (hiddenNodeStatus <= 0) {
                continue;
            }

            totalRequiredMemory += this.getRegionMemory(hiddenNode.id, Math.ceil(hiddenNodeStatus / 2));
        }

        while (totalRequiredMemory > regionMemoryLimit) {
            if (this.unloadCompletedHiddenRegion(hiddenNodes)) {
                totalRequiredMemory = this.getRegionPlannedVisibleMemory(plannedVisibleNodes) + this.getLoadedHiddenRegionMemory(hiddenNodes);
                continue;
            }

            if (plannedVisibleNodes.length == 0) {
                break;
            }

            var droppedVisibleNode = plannedVisibleNodes.pop();

            allowedVisibleNodeIds.delete(droppedVisibleNode.node.id);

            if ((this._displayRegionDataStatus?.[droppedVisibleNode.node.id]?.[0] ?? 0) > 0) {
                this.unloadRegion(droppedVisibleNode.node.id);
            }

            totalRequiredMemory = this.getRegionPlannedVisibleMemory(plannedVisibleNodes) + this.getLoadedHiddenRegionMemory(hiddenNodes);
        }

        return {
            allowedVisibleNodeIds,
            memoryPressureHit: plannedVisibleNodes.length < visibleNodes.length
        };
    }

    private getRegionPlannedVisibleMemory(plannedVisibleNodes: { node: FrustumStructures.TreeNode; memoryRequired: number; }[]): number {
        var totalMemory = 0;

        for (var x = 0; x < plannedVisibleNodes.length; x++) {
            totalMemory += plannedVisibleNodes[x].memoryRequired;
        }

        return totalMemory;
    }

    private getLoadedHiddenRegionMemory(hiddenNodes: FrustumStructures.TreeNode[]): number {
        var totalMemory = 0;

        hiddenNodes = hiddenNodes ?? [];

        for (var z = 0; z < hiddenNodes.length; z++) {
            var hiddenNode = hiddenNodes[z];
            var hiddenNodeStatus = this._displayRegionDataStatus?.[hiddenNode.id]?.[0] ?? 0;

            if (hiddenNodeStatus <= 0) {
                continue;
            }

            totalMemory += this.getRegionMemory(hiddenNode.id, Math.ceil(hiddenNodeStatus / 2));
        }

        return totalMemory;
    }

    private unloadCompletedHiddenRegion(hiddenNodes: FrustumStructures.TreeNode[]): boolean {
        hiddenNodes = hiddenNodes ?? [];

        for (var z = hiddenNodes.length - 1; z >= 0; z--) {
            var nodeToDisplay: FrustumStructures.TreeNode = hiddenNodes[z];
            var nodeStatus = this._displayRegionDataStatus?.[nodeToDisplay.id]?.[0] ?? 0;

            if (nodeStatus > 0) {
                this.unloadRegion(nodeToDisplay.id);
                return true;
            }
        }

        return false;
    }

    private canDisplayRegionTargetStatus(nodeId: string, targetNodeStatus: number): boolean {
        if (targetNodeStatus <= 0) {
            return false;
        }

        var type = this._displayRegionDataTypes[nodeId]?.[targetNodeStatus / 2]?.toLowerCase?.() ?? "";

        if (type == "") {
            return false;
        }

        if ((type === "mesh") && !this._contentLayers.mesh) return false;
        if (this._isCadRegionType(type) && !this._canLoadCadRegions()) return false;
        if ((type === "point cloud" || type === "pointcloud") && !this._contentLayers.pointCloud) return false;

        return true;
    }

    calculateRegionDisplayLevels(nodes: FrustumStructures.TreeNode[]) {

        var memoryNeeded = 0;

        var lodLevelNodes: { [lodLevel: number]: FrustumStructures.TreeNode[]; } = {};

        var maxLodLevel = this.lodDistances.length;
        for (var x = 0; x < nodes.length; x++) {
            maxLodLevel = Math.max(maxLodLevel, this.getConfiguredLodLevelCount(nodes[x].id), this.getMaxAvailableRegionLodLevel(nodes[x].id));
        }

        for (var x = 1; x < maxLodLevel + 1; x++) {
            lodLevelNodes[x] = [];
        }

        for (var x = 0; x < nodes.length; x++) {

            var node = nodes[x];

            // To do, return 0 as the highest rez level, instead of larger number!
            //(node as any).lodLevel = this.lodDistances.length - (node as any).lodLevel;
            (node as any).lodLevel = this.getClosestLodLevel(node);

            if ((node as any).lodLevel >= 0) {

                var memory = this.getRegionMemory(node.id, (node as any).lodLevel);

                memoryNeeded += memory;

                lodLevelNodes[(node as any).lodLevel] ||= [];
                lodLevelNodes[(node as any).lodLevel].push(node);
            }
        }

        if (memoryNeeded > this.getEffectiveRegionMemoryLimit()) {
            // this display request is too large, so we need to lower the LOD for the nodes so that it fits
            for (var lodLevel = 1; lodLevel < maxLodLevel + 1; lodLevel++) {
                {
                    lodLevelNodes[lodLevel] ||= [];
                    for (var x = lodLevelNodes[lodLevel].length - 1; x >= 0; x--) {

                        // If we're at highest mesh level, always keep 1
                        if (lodLevel == 0 && (x < this._minNumMeshes))
                            continue

                        var node = lodLevelNodes[lodLevel][x];

                        var nextLevel = this.getNextLowestLODLevel(node);

                        if (nextLevel != -1) {
                            var prevLevel = (node as any).lodLevel;
                            (node as any).lodLevel = nextLevel;
                            lodLevelNodes[lodLevel].splice(x, 1);
                            lodLevelNodes[(node as any).lodLevel] ||= [];
                            lodLevelNodes[(node as any).lodLevel].unshift(node);

                            memoryNeeded -= this.getRegionMemory(node.id, prevLevel) - this.getRegionMemory(node.id, nextLevel);

                            if (memoryNeeded <= this.getEffectiveRegionMemoryLimit())
                                return memoryNeeded;
                        }
                    }
                }
            }
        }
    }

    unloadRegion(regionId: string) {
        var processedStatus = this._displayRegionDataProcessedStatus[regionId];
        var currentStatus = this._displayRegionDataStatus?.[regionId]?.[0] ?? 0;
        var nodeStatus = processedStatus > 0 ? processedStatus : currentStatus;
        var disposedObjectIds = this._meshManager.getRegionObjectIdsByBlockPage(regionId, "0");
        this._meshManager.disposeRegionData(regionId, "0");
        this._selectionManager.setObjectsToMaintainSelection(disposedObjectIds);
        this._overrideManager.forgetAssets(disposedObjectIds);

        if (nodeStatus > 0) {
            var lodLevel = Math.ceil(nodeStatus / 2);

            this._displayRegionDataProcessedStatus[regionId] = 0;

            this._regionDataLength = Math.max(0, this._regionDataLength - this.getRegionMemory(regionId, lodLevel));

            for (var s = 1; s < this._regionSegmentCount[regionId]; s++) {

                this._meshManager.disposeRegionData(regionId, s.toString());
                this._displayRegionDataStatus[regionId][s] = 0;
            }

            if (this.showRegionBoxes) {
                this._pointMeshes[Number(regionId)].setEnabled(false);
                this._regionMeshes[Number(regionId)].setEnabled(false);
            }
        }

        this.clearTrackedRegionPageLengths(regionId);
        this._displayRegionDataStatus[regionId][0] = 0;
        this.logGeneratedMeshLodMemoryDiagnostics();
    }

    getRegionMemory(block: string, lodLevel: number) : number{

        if (this._regionDataLengths[block] == null || this._regionDataLengths[block][lodLevel] == null) {

            return 0;
        }

        return this._regionDataLengths[block][lodLevel];
    }

    private getDisplayMemoryLimit(): number {
        return DisplayManager.MEMORY_PRESSURE_MEGABYTE_LIMIT;
    }

    private getRegionDisplayMemoryLimit(): number {
        return DisplayManager.MEMORY_REGION_PRESSURE_MEGABYTE_LIMIT;
    }

    private getEffectiveAssetMemoryLimit(): number {
        return Math.max(0, this.getDisplayMemoryLimit() - this._regionDataLength);
    }

    private getEffectiveRegionMemoryLimit(): number {
        const remainingSharedMemory = Math.max(0, this.getDisplayMemoryLimit() - this._dataLength);
        return Math.min(this.getRegionDisplayMemoryLimit(), remainingSharedMemory);
    }

    private getTrackedRegionMemory(blockId: string): number {
        var totalMemory = 0;
        var segmentCount = Math.max(this._regionSegmentCount[blockId] || 1, 1);

        for (var pageIndex = 0; pageIndex < segmentCount; pageIndex++) {
            totalMemory += this._pageLength[blockId + "_" + pageIndex.toString()] || 0;
        }

        return totalMemory;
    }

    private reconcileTrackedRegionMemory(blockId: string, lodLevel: number): void {
        var actualMemory = this.getTrackedRegionMemory(blockId);

        if (actualMemory <= 0) {
            return;
        }

        var reservedMemory = this.getRegionMemory(blockId, lodLevel);

        if (this._regionDataLengths[blockId] == null) {
            this._regionDataLengths[blockId] = {};
        }

        this._regionDataLengths[blockId][lodLevel] = actualMemory;
        this._regionDataLength = Math.max(0, this._regionDataLength + actualMemory - reservedMemory);
        this.logGeneratedMeshLodMemoryDiagnostics();
    }

    private clearTrackedRegionPageLengths(blockId: string): void {
        var segmentCount = Math.max(this._regionSegmentCount[blockId] || 1, 1);

        for (var pageIndex = 0; pageIndex < segmentCount; pageIndex++) {
            this._pageLength[blockId + "_" + pageIndex.toString()] = 0;
        }
    }

    public logGeneratedMeshLodMemoryDiagnostics(force: boolean = false): void {
        if (!DisplayManager.LOG_GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS) {
            return;
        }

        const now = Date.now();
        if (!force && now - this._lastGeneratedMeshLodMemoryDiagnosticUtc < DisplayManager.GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS_INTERVAL_MS) {
            return;
        }

        this._lastGeneratedMeshLodMemoryDiagnosticUtc = now;

        const summary: { [lodLevel: number]: { loadedPages: number; loadingPages: number; memoryMB: number; }; } = {};

        for (const blockId of Object.keys(this._displayRegionDataStatus ?? {})) {
            const status = this._displayRegionDataStatus[blockId]?.[0] ?? 0;
            if (status <= 0) {
                continue;
            }

            const lodLevel = Math.ceil(status / 2);
            const loadType = this._displayRegionDataTypes[blockId]?.[lodLevel]?.toLowerCase?.() ?? "";
            if (loadType !== "pagedglb" && loadType !== "pagedfbx") {
                continue;
            }

            summary[lodLevel] ||= {
                loadedPages: 0,
                loadingPages: 0,
                memoryMB: 0,
            };

            if (status % 2 === 0) {
                summary[lodLevel].loadedPages++;
                summary[lodLevel].memoryMB += this.getRegionMemory(blockId, lodLevel);
            }
            else {
                summary[lodLevel].loadingPages++;
            }
        }

        let maxLodLevel = this.lodDistances.length;
        for (const lodLevel of Object.keys(summary)) {
            maxLodLevel = Math.max(maxLodLevel, Number(lodLevel));
        }
        for (const blockId of Object.keys(this._regionDataLengths ?? {})) {
            maxLodLevel = Math.max(maxLodLevel, this.getMaxAvailableRegionLodLevel(blockId));
        }

        const rows = Array.from({ length: maxLodLevel }, (_, index) => index + 1).map((lodLevel) => {
            const item = summary[lodLevel] ?? { loadedPages: 0, loadingPages: 0, memoryMB: 0 };
            return {
                lod: lodLevel,
                loadedPages: item.loadedPages,
                loadingPages: item.loadingPages,
                memoryMB: Number(item.memoryMB.toFixed(3)),
            };
        });

        console.table(rows);
        console.log("[GeneratedMeshLODMemory]", {
            totalLoadedMemoryMB: Number(rows.reduce((sum, row) => sum + row.memoryMB, 0).toFixed(3)),
            totalTrackedRegionMemoryMB: Number((this._regionDataLength ?? 0).toFixed(3)),
            diagnosticToggle: "DisplayManager.LOG_GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS",
        });
    }

    private initializeGeneratedMeshLodMemoryDiagnostics(): void {
        if (typeof window === "undefined") {
            return;
        }

        (window as any).generatedMeshLodMemoryDiagnostics = {
            enable: () => {
                DisplayManager.LOG_GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS = true;
                this.logGeneratedMeshLodMemoryDiagnostics(true);
            },
            disable: () => {
                DisplayManager.LOG_GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS = false;
            },
            log: () => {
                this.logGeneratedMeshLodMemoryDiagnostics(true);
            },
            setIntervalMs: (intervalMs: number) => {
                if (Number.isFinite(intervalMs) && intervalMs >= 0) {
                    DisplayManager.GENERATED_MESH_LOD_MEMORY_DIAGNOSTICS_INTERVAL_MS = intervalMs;
                }
            },
        };
    }

    freeMemory(memoryTofree: number) {

        var visibleNodes = this._visibleNodes;
        var hiddenNodes = this._hiddenNodes;
        var memoryFreed = 0;

        if (hiddenNodes.length > 0) {

            for (var z = hiddenNodes.length - 1; z >= 0; z--) {
                var node: FrustumStructures.TreeNode = hiddenNodes[z];

                if ((this._displayDataStatus[node.blockId][node.id] == 2)) {

                    var disposedObjectIds = this._meshManager.getObjectIdsByBlockPage(node.blockId, node.id);
                    this._selectionManager.setObjectsToMaintainSelection(disposedObjectIds);
                    this._meshManager.setObjectsToMaintainHidden(disposedObjectIds);
                    // dispose the meshes that correspond to this block/page id.
                    this._meshManager.disposePageData(node.blockId, node.id);
                    this._overrideManager.forgetAssets(disposedObjectIds);

                    var disposeSize = this._pageLength[node.blockId + "_" + node.id];
                    this._pageLength[node.blockId + "_" + node.id] = 0;
                    this._dataLength -= disposeSize;
                    memoryFreed += disposeSize;

                    this._displayDataStatus[node.blockId][node.id] = 0;
                    if (DebugOptions.valueOf()) {
                        this._meshManager.setBoxLoaded(node.blockId, -1);
                    }
                }

                if (memoryFreed >= memoryTofree) {

                    return;
                }
            }
        }

        for (var x = visibleNodes.length - 1; x >= 0; x--) {

            var node: FrustumStructures.TreeNode = visibleNodes[x];

            if (this._displayDataStatus[node.blockId][node.id] == 2) {

                var disposedObjectIds = this._meshManager.getObjectIdsByBlockPage(node.blockId, node.id);
                this._selectionManager.setObjectsToMaintainSelection(disposedObjectIds);
                this._meshManager.setObjectsToMaintainHidden(disposedObjectIds);

                this._meshManager.disposePageData(node.blockId, node.id);
                this._overrideManager.forgetAssets(disposedObjectIds);

                var disposeSize = this._pageLength[node.blockId + "_" + node.id];
                this._pageLength[node.blockId + "_" + node.id] = 0;
                this._dataLength -= disposeSize;
                memoryFreed += disposeSize;
                this._displayDataStatus[node.blockId][node.id] = 0;

                if (DebugOptions.valueOf()) {
                    this._meshManager.setBoxLoaded(node.blockId, -1);
                }

                if (memoryFreed >= memoryTofree) {

                    return;
                }
            }
        }
    }

    private _getAssetMemoryPressurePages(nodes: FrustumStructures.TreeNode[], visible: boolean): AssetMemoryPressurePage[] {
        const pages: AssetMemoryPressurePage[] = [];
        nodes = nodes ?? [];
        for (let priority = 0; priority < nodes.length; priority++) {
            const node = nodes[priority];
            const key = `${node.blockId}_${node.id}`;
            const assetLayerId = this._assetLayerByNodeKey[key];
            const geometryEnabled = assetLayerId == null || this._assetSetManager.isSetGeometryEnabled(assetLayerId);
            pages.push({
                key,
                priority,
                loaded: this._displayDataStatus?.[node.blockId]?.[node.id] === 2,
                eligible: visible && geometryEnabled && node.octantProjectedSize >= 5,
                memory: Math.max(0, this._pageLength[key] ?? 0),
            });
        }
        return pages;
    }

    private _disposeAssetMemoryPressurePages(
        pageKeys: string[],
        visibleNodes: FrustumStructures.TreeNode[],
        hiddenNodes: FrustumStructures.TreeNode[]
    ): void {
        if (pageKeys.length === 0) return;

        const requestedKeys = new Set(pageKeys);
        const nodesByKey = new Map<string, FrustumStructures.TreeNode>();
        for (const node of [...(visibleNodes ?? []), ...(hiddenNodes ?? [])]) {
            const key = `${node.blockId}_${node.id}`;
            if (requestedKeys.has(key)) nodesByKey.set(key, node);
        }

        this._meshManager.beginCylinderInstanceDisposalBatch();
        try {
            for (const pageKey of requestedKeys) {
                const node = nodesByKey.get(pageKey);
                if (!node || this._displayDataStatus?.[node.blockId]?.[node.id] !== 2) continue;

                this._disposeTrackedAssetPage(node.blockId, node.id);
                this._displayDataStatus[node.blockId][node.id] = 0;
                if (DebugOptions.valueOf()) this._meshManager.setBoxLoaded(node.blockId, -1);
            }
        }
        finally {
            this._meshManager.endCylinderInstanceDisposalBatch();
        }
    }

    freeLowPriorityMemory(
        memoryToMakeRoomFor: number,
        incomingBlockId?: string,
        incomingPageId?: string,
        incomingPriority?: number
    ): boolean {
        void memoryToMakeRoomFor; // The existing policy admits the first page that crosses the limit.

        const memoryLimit = this.getEffectiveAssetMemoryLimit();
        if (this._dataLength < memoryLimit) return true;

        const visibleNodes = this._copyNodeArray<FrustumStructures.TreeNode>(this._visibleNodes);
        const hiddenNodes = this._copyNodeArray<FrustumStructures.TreeNode>(this._hiddenNodes);
        const visiblePages = this._getAssetMemoryPressurePages(visibleNodes, true);
        const hiddenPages = this._getAssetMemoryPressurePages(hiddenNodes, false);
        const incomingKey = incomingBlockId != null && incomingPageId != null
            ? `${incomingBlockId}_${incomingPageId}`
            : null;
        const currentPriority = incomingKey == null
            ? (incomingPriority ?? -1)
            : (visiblePages.find(page => page.key === incomingKey)?.priority ?? incomingPriority ?? -1);
        const plan = planAssetPageAdmission(
            this._dataLength,
            memoryLimit,
            currentPriority,
            visiblePages,
            hiddenPages
        );

        if (!plan.allowed) return false;

        this._disposeAssetMemoryPressurePages(plan.evictPageKeys, visibleNodes, hiddenNodes);
        return this._dataLength < memoryLimit;
    }

    private getAssetDisplayProgressCounts(visibleNodes: FrustumStructures.TreeNode[]): { totalCompleteInFrustum: number; totalDisplayCandidates: number; } {

        var totalCompleteInFrustum = 0;
        var totalDisplayCandidates = 0;
        var hitIncompleteCandidate = false;

        visibleNodes = visibleNodes ?? [];

        for (var x = 0; x <= visibleNodes.length - 1; x++) {

            var node: FrustumStructures.TreeNode = visibleNodes[x];
            var nodeKey = `${node.blockId}_${node.id}`;
            var assetLayerId = this._assetLayerByNodeKey[nodeKey];

            if (assetLayerId != null && !this._assetSetManager.isSetGeometryEnabled(assetLayerId)) {
                continue;
            }

            if (node.octantProjectedSize < 5) {
                continue;
            }

            totalDisplayCandidates++;

            if (this._displayDataStatus == null || this._displayDataStatus[node.blockId] == null ||
                this._displayDataStatus[node.blockId][node.id] == null) {
                hitIncompleteCandidate = true;
                continue;
            }

            if (!hitIncompleteCandidate && this._displayDataStatus[node.blockId][node.id] == 2) {
                totalCompleteInFrustum++;
            }
            else if (!hitIncompleteCandidate && this._displayDataStatus[node.blockId][node.id] == 1) {
                continue;
            }
            else {
                hitIncompleteCandidate = true;
            }
        }

        return {
            totalCompleteInFrustum,
            totalDisplayCandidates
        };
    }

    private getRegionProgressTargetStatus(node: FrustumStructures.TreeNode): number {
        var lodLevel = this.getClosestLodLevel(node);

        if (lodLevel <= 0) {
            return 0;
        }

        return lodLevel * 2;
    }

    private getRegionDisplayProgressCounts(visibleNodes: FrustumStructures.TreeNode[]): { totalCompleteInFrustum: number; totalDisplayCandidates: number; } {

        const allowMesh = this._canLoadCadRegions();
        const allowPC = this._contentLayers.pointCloud;
        const allowSmartMesh = this._contentLayers.mesh;

        if (!allowMesh && !allowPC && !allowSmartMesh) {
            return {
                totalCompleteInFrustum: 0,
                totalDisplayCandidates: 0
            };
        }

        var totalCompleteInFrustum = 0;
        var totalDisplayCandidates = 0;
        visibleNodes = visibleNodes ?? [];

        for (var x = 0; x <= visibleNodes.length - 1; x++) {

            var node = visibleNodes[x];
            var targetNodeStatus = this.getRegionProgressTargetStatus(node);

            if (targetNodeStatus <= 0) {
                continue;
            }

            var type = this._displayRegionDataTypes[node.id]?.[targetNodeStatus / 2]?.toLowerCase?.() ?? "";

            if (type == "") {
                continue;
            }

            if (type === "mesh" && !allowSmartMesh) {
                continue;
            }

            if ((type === "glb" || type === "pagedglb" || type === "pagedfbx") && !allowMesh) {
                continue;
            }

            if ((type === "point cloud" || type === "pointcloud") && !allowPC) {
                continue;
            }

            totalDisplayCandidates++;

            var nodeStatus = this._displayRegionDataStatus?.[node.id]?.[0];

            if (nodeStatus == null) {
                continue;
            }

            if (nodeStatus > 0 && nodeStatus % 2 == 0) {
                totalCompleteInFrustum++;
            }
        }

        return {
            totalCompleteInFrustum,
            totalDisplayCandidates
        };
    }

    private getDisplayProgressCounts(assetVisibleNodes?: FrustumStructures.TreeNode[], regionVisibleNodes?: FrustumStructures.TreeNode[]): { totalCompleteInFrustum: number; totalDisplayCandidates: number; completionPercentage: number; } {

        const assetCounts = this._contentLayers.assets
            ? this.getAssetDisplayProgressCounts(assetVisibleNodes ?? this._visibleNodes ?? [])
            : { totalCompleteInFrustum: 0, totalDisplayCandidates: 0 };
        const regionCounts = this.getRegionDisplayProgressCounts(regionVisibleNodes ?? this._regionVisibleNodes ?? []);
        const totalCompleteInFrustum = assetCounts.totalCompleteInFrustum + regionCounts.totalCompleteInFrustum;
        const totalDisplayCandidates = assetCounts.totalDisplayCandidates + regionCounts.totalDisplayCandidates;

        return {
            totalCompleteInFrustum,
            totalDisplayCandidates,
            completionPercentage: totalDisplayCandidates <= 0 ? 100 : (totalCompleteInFrustum / totalDisplayCandidates) * 100
        };
    }

    private getDisplayCompletionPercentage(assetVisibleNodes?: FrustumStructures.TreeNode[], regionVisibleNodes?: FrustumStructures.TreeNode[]): number {
        return this.getDisplayProgressCounts(assetVisibleNodes, regionVisibleNodes).completionPercentage;
    }

    private setAssetMemoryPressureHit(memoryPressureHit: boolean): boolean {
        this._assetMemoryPressureHit = memoryPressureHit;
        this._memoryPressureHit = this._assetMemoryPressureHit || this._regionMemoryPressureHit;
        return this._memoryPressureHit;
    }

    private setRegionMemoryPressureHit(memoryPressureHit: boolean): boolean {
        this._regionMemoryPressureHit = memoryPressureHit;
        this._memoryPressureHit = this._assetMemoryPressureHit || this._regionMemoryPressureHit;
        return this._memoryPressureHit;
    }

    private getLoadedSceneObjectCounts(): {
        objectInfoCount: number;
        uniqueObjectCount: number;
        masterMeshCount: number;
        sceneMeshCount: number;
        enabledSceneMeshCount: number;
        logicalVertexCount: number;
        uniqueGeometryVertexCount: number;
        thinInstanceVertexCount: number;
        instancedMeshVertexCount: number;
    } {
        const masterMeshToObjectInfo = this._meshManager?._masterMeshToObjectInfo ?? {};
        const uniqueObjectIds = new Set<string>();
        let objectInfoCount = 0;

        for (const objectInfos of Object.values(masterMeshToObjectInfo)) {
            if (!Array.isArray(objectInfos)) {
                continue;
            }

            objectInfoCount += objectInfos.length;

            for (const objectInfo of objectInfos) {
                if (objectInfo?.objectId != null) {
                    uniqueObjectIds.add(objectInfo.objectId);
                }
            }
        }

        const sceneMeshes: BABYLON.AbstractMesh[] = (this._meshManager as any)?._babScene?.meshes ?? [];
        let enabledSceneMeshCount = 0;
        let logicalVertexCount = 0;
        let uniqueGeometryVertexCount = 0;
        let thinInstanceVertexCount = 0;
        let instancedMeshVertexCount = 0;

        for (const sceneMesh of sceneMeshes) {
            if (sceneMesh != null && sceneMesh.isEnabled()) {
                enabledSceneMeshCount++;

                if (sceneMesh instanceof BABYLON.InstancedMesh) {
                    const sourceVertexCount = sceneMesh.sourceMesh?.getTotalVertices() ?? 0;
                    logicalVertexCount += sourceVertexCount;
                    instancedMeshVertexCount += sourceVertexCount;
                    continue;
                }

                const vertexCount = typeof (sceneMesh as any).getTotalVertices === 'function'
                    ? Math.max(0, (sceneMesh as any).getTotalVertices() ?? 0)
                    : 0;

                if (vertexCount <= 0) {
                    continue;
                }

                const thinInstanceCount = sceneMesh instanceof BABYLON.Mesh
                    ? Math.max(0, sceneMesh.thinInstanceCount ?? 0)
                    : 0;

                if (thinInstanceCount > 0) {
                    const expandedVertexCount = vertexCount * thinInstanceCount;

                    logicalVertexCount += expandedVertexCount;
                    uniqueGeometryVertexCount += vertexCount;
                    thinInstanceVertexCount += expandedVertexCount;
                    continue;
                }

                logicalVertexCount += vertexCount;
                uniqueGeometryVertexCount += vertexCount;
            }
        }

        return {
            objectInfoCount,
            uniqueObjectCount: uniqueObjectIds.size,
            masterMeshCount: Object.keys(masterMeshToObjectInfo).length,
            sceneMeshCount: sceneMeshes.length,
            enabledSceneMeshCount,
            logicalVertexCount,
            uniqueGeometryVertexCount,
            thinInstanceVertexCount,
            instancedMeshVertexCount
        };
    }

    private fireCombinedDisplayProgress(): void {

        if (this._displayEventHandler == null) {
            return;
        }

        const hasWorkersInProgress = this._pool != null && this._pool._inUseWorkers.length > 0;
        const combinedMemoryPressureHit = this._assetMemoryPressureHit || this._regionMemoryPressureHit;
        const { totalCompleteInFrustum, totalDisplayCandidates, completionPercentage } = this.getDisplayProgressCounts();

        if (totalDisplayCandidates == 0) {
            if (hasWorkersInProgress) {
                return;
            }

            this._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYFINISHED, 100);
            this._displaying = true;
            return;
        }

        if ((totalDisplayCandidates > totalCompleteInFrustum) && combinedMemoryPressureHit && !hasWorkersInProgress) {
            this._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYMEMORYLIMIT, completionPercentage);
            this._displaying = hasWorkersInProgress;
            return;
        }

        const isDisplayComplete = totalCompleteInFrustum == totalDisplayCandidates;
        this._displayEventHandler.fireEvent(Enumerations.EventType.DISPLAYFINISHED, completionPercentage);
        this._displaying = !isDisplayComplete;
    }

    public processMemoryPressureDisplay(displayManager: DisplayManager, visibleNodes: FrustumStructures.TreeNode[], hiddenNodes: FrustumStructures.TreeNode[]) {

        displayManager._visibleNodes = visibleNodes;
        displayManager._hiddenNodes = hiddenNodes;

        if (!displayManager._contentLayers.assets) {
            displayManager.setAssetMemoryPressureHit(false);
            displayManager.fireCombinedDisplayProgress();
            return;
        }

        var memroyPressureHit = false;

        // Once over budget, reconcile all loaded pages to the same ordered
        // prefix that a blank load would produce. This batches the lower-
        // priority eviction and retains the first page that crossed the cap.
        if (displayManager._dataLength > displayManager.getEffectiveAssetMemoryLimit()) {
            const visiblePageNodes = displayManager._copyNodeArray(visibleNodes);
            const hiddenPageNodes = displayManager._copyNodeArray(hiddenNodes);
            const plan = planAssetMemoryPressureResidency(
                displayManager._dataLength,
                displayManager.getEffectiveAssetMemoryLimit(),
                displayManager._getAssetMemoryPressurePages(visiblePageNodes, true),
                displayManager._getAssetMemoryPressurePages(hiddenPageNodes, false)
            );

            displayManager._disposeAssetMemoryPressurePages(
                plan.evictPageKeys,
                visiblePageNodes,
                hiddenPageNodes
            );
            memroyPressureHit = plan.memoryPressureHit
                || displayManager._dataLength > displayManager.getEffectiveAssetMemoryLimit();
        }

        var totalCompleteInFrustum = 0;
        var totalDisplayCandidates = 0;
        var hitIncompleteCandidate = false;

        if (DebugOptions.valueOf()) {
            displayManager._meshManager.clearBoxPageCounts();
        }

        for (var x = 0; x <= visibleNodes.length - 1; x++) {

            var node: FrustumStructures.TreeNode = visibleNodes[x];

            const nodeKey = `${node.blockId}_${node.id}`;
            const assetLayerId = displayManager._assetLayerByNodeKey[nodeKey];

            if (assetLayerId != null && !displayManager._assetSetManager.isSetGeometryEnabled(assetLayerId)) {
                continue; // skip loading for this node
            }

            if (node.octantProjectedSize < 5) {
                continue;
            }

            totalDisplayCandidates++;

            if (displayManager._displayDataStatus == null || displayManager._displayDataStatus[node.blockId] == null ||
                displayManager._displayDataStatus[node.blockId][node.id] == null) {

                // This has only been seen to happen when you kill a coordinator (from changing range view perhaps),
                // and the coordinator still gasps back a callback to load its content. In this case
                // just ignore any nodes that get asked to be loaded for which we do not have a display status.
                return;
            }
            var nodeStatus = displayManager._displayDataStatus[node.blockId][node.id];

            if (DebugOptions.valueOf()) {
                displayManager._meshManager.setBoxPageVisible(node.blockId, 1);
            }

            if (nodeStatus == 0) {
                hitIncompleteCandidate = true;

                if ((displayManager._pool == null || displayManager._pool._availableWorkers.length > 0) && (displayManager._dataLength <= displayManager.getEffectiveAssetMemoryLimit())) {
                    displayManager.queueDisplayOctantBatch(visibleNodes, x);
                }
            }
            else if (nodeStatus == 1) {
                continue;
            }
            else if (nodeStatus == 2) {
                if (!hitIncompleteCandidate) {
                    totalCompleteInFrustum++;
                }
            }
            else {
                hitIncompleteCandidate = true;
            }
        }

        displayManager.setAssetMemoryPressureHit(memroyPressureHit);
        displayManager.fireCombinedDisplayProgress();
    }

    public processDisplayOctantContent(displayParameters: any, blockId: string, pageId: string, priority : number) {

        try {
            const authContext = getCurrentWorkerAuthContext();

            const sourceAssetSetId = this._assetLayerByNodeKey[`${blockId}_${pageId}`];
            var data : Data.GraphicData.WebWorkerDataRequest = {

                BlockId: blockId,
                PageId: pageId,
                DisplayListId: displayParameters,
                ProjectId: DisplayWorker.ProjectID,
                Url: DisplayWorker.DisplayRequestURL,
                OctreeUrl: DisplayWorker.OctreeRequestURL,
                DecoderUrl: DisplayWorker.DisplayDecoderURL,
                ActivatedNamedLists: DisplayWorker.ActivatedNamedLists,
                AllNamedLists: DisplayWorker.AllNamedLists,
                UseDefaultMaterial: DisplayWorker.UseDefaultMaterial,
                UseXRayMaterial: DisplayManager.APPLY_XRAY_IN_WORKER_PARSE,
                WebWorkerNumber: 0,
                DisplayGeneration: this._displayRequestGeneration,
                AccessToken: authContext.accessToken,
                LoadDataType: "",
                LOD: 1,
                Type: 2,
                ContentDisplayPriority: priority,
                SourceAssetSetId: sourceAssetSetId,
                GeometryOwnershipRevision: this._assetGeometryOwnership.revision,
                GeometryOwnershipEpoch: sourceAssetSetId
                    ? this._assetSetManager.getGeometryOwnershipEpoch(sourceAssetSetId)
                    : 0,
                AuthContext: authContext,
            };

            if (this._tasksForCompletePage[blockId] == null) {
                this._tasksForCompletePage[blockId] = {};
            }

            this._tasksForCompletePage[blockId][pageId] = 1;
            this._pool.addTask(data);
            this._numGraphics++;
        }
        catch (error) {

            console.log("Display Error " + error);
        }
    }

    public setDisplayPageRequestBatchSize(size: number) {
        if (Number.isFinite(size) && size > 0) {
            this._displayPageRequestBatchSize = Math.max(1, Math.floor(size));
        }
    }

    public getDisplayPageRequestBatchSize(): number {
        return this._displayPageRequestBatchSize;
    }

    public setPointCloudPageRequestBatchSize(size: number) {
        if (Number.isFinite(size) && size > 0) {
            this._pointCloudPageRequestBatchSize = Math.max(1, Math.floor(size));
        }
    }

    public getPointCloudPageRequestBatchSize(): number {
        return this._pointCloudPageRequestBatchSize;
    }

    private queueDisplayOctantBatch(visibleNodes: FrustumStructures.TreeNode[], startIndex: number) {

        try {
            const authContext = getCurrentWorkerAuthContext();
            var pageRequests: Data.GraphicData.WebWorkerPageRequest[] = [];
            var batchSize = Math.max(1, Math.floor(this._displayPageRequestBatchSize || 1));

            for (var i = startIndex; i < visibleNodes.length && pageRequests.length < batchSize; i++) {
                var node = visibleNodes[i];

                if (node == null) {
                    continue;
                }

                var nodeKey = `${node.blockId}_${node.id}`;
                var assetLayerId = this._assetLayerByNodeKey[nodeKey];

                if (assetLayerId != null && !this._assetSetManager.isSetGeometryEnabled(assetLayerId)) {
                    continue;
                }

                if (this._displayDataStatus == null || this._displayDataStatus[node.blockId] == null || this._displayDataStatus[node.blockId][node.id] == null) {
                    continue;
                }

                if (node.octantProjectedSize < 5 || this._displayDataStatus[node.blockId][node.id] != 0) {
                    continue;
                }

                if (this._displayDataCache[node.blockId] == null || this._displayDataCache[node.blockId][node.id] == null) {
                    continue;
                }

                this._displayDataStatus[node.blockId][node.id] = 1;

                if (this._tasksForCompletePage[node.blockId] == null) {
                    this._tasksForCompletePage[node.blockId] = {};
                }

                this._tasksForCompletePage[node.blockId][node.id] = 1;
                pageRequests.push({
                    BlockId: node.blockId,
                    PageId: node.id,
                    DisplayListId: this._displayDataCache[node.blockId][node.id],
                    MetadataFileId: this._displayRegionMetadataDataCache[node.blockId]?.[node.id],
                    ContentDisplayPriority: i,
                    SourceAssetSetId: assetLayerId,
                    GeometryOwnershipRevision: this._assetGeometryOwnership.revision,
                    GeometryOwnershipEpoch: assetLayerId
                        ? this._assetSetManager.getGeometryOwnershipEpoch(assetLayerId)
                        : 0,
                });
            }

            if (pageRequests.length == 0) {
                return;
            }

            var firstPageRequest = pageRequests[0];
            var data: Data.GraphicData.WebWorkerDataRequest = {
                BlockId: firstPageRequest.BlockId,
                PageId: firstPageRequest.PageId,
                DisplayListId: firstPageRequest.DisplayListId,
                MetadataFileId: firstPageRequest.MetadataFileId,
                PageRequests: pageRequests,
                ProjectId: DisplayWorker.ProjectID,
                Url: DisplayWorker.DisplayRequestURL,
                OctreeUrl: DisplayWorker.OctreeRequestURL,
                DecoderUrl: DisplayWorker.DisplayDecoderURL,
                ActivatedNamedLists: DisplayWorker.ActivatedNamedLists,
                AllNamedLists: DisplayWorker.AllNamedLists,
                UseDefaultMaterial: DisplayWorker.UseDefaultMaterial,
                UseXRayMaterial: DisplayManager.APPLY_XRAY_IN_WORKER_PARSE,
                WebWorkerNumber: 0,
                DisplayGeneration: this._displayRequestGeneration,
                AccessToken: authContext.accessToken,
                LoadDataType: "",
                LOD: 1,
                Type: 2,
                ContentDisplayPriority: firstPageRequest.ContentDisplayPriority,
                SourceAssetSetId: firstPageRequest.SourceAssetSetId,
                GeometryOwnershipRevision: firstPageRequest.GeometryOwnershipRevision,
                GeometryOwnershipEpoch: firstPageRequest.GeometryOwnershipEpoch,
                AuthContext: authContext,
            };

            this._pool.addTask(data);
            this._numGraphics += pageRequests.length;
        }
        catch (error) {

            console.log("Display Error " + error);
        }
    }

    private queueDisplayRegionOctantBatch(
        visibleNodes: FrustumStructures.TreeNode[],
        startIndex: number,
        allowedVisibleNodeIds: Set<string>): boolean {

        try {
            const authContext = getCurrentWorkerAuthContext();
            const pageRequests: Data.GraphicData.WebWorkerPageRequest[] = [];
            let batchSize = Math.max(1, Math.floor(this._displayPageRequestBatchSize || 1));
            let batchLoadType = "";

            for (let i = startIndex; i < visibleNodes.length && pageRequests.length < batchSize; i++) {
                const node = visibleNodes[i];
                if (node == null || !allowedVisibleNodeIds.has(node.id)) {
                    continue;
                }

                const targetLodLevel = this.getClosestLodLevel(node);
                if (targetLodLevel <= 0) {
                    continue;
                }

                const targetNodeStatus = targetLodLevel * 2;
                const loadType = this._displayRegionDataTypes[node.id]?.[targetLodLevel];
                const normalizedLoadType = loadType?.toLowerCase?.() ?? "";
                const normalizedBatchLoadType = normalizedLoadType === "pointcloud" ? "point cloud" : normalizedLoadType;
                if (normalizedBatchLoadType !== "mesh"
                    && normalizedBatchLoadType !== "pagedglb"
                    && normalizedBatchLoadType !== "pagedfbx"
                    && normalizedBatchLoadType !== "point cloud") {
                    continue;
                }

                if (pageRequests.length > 0 && normalizedBatchLoadType !== batchLoadType) {
                    continue;
                }

                if (!this.canDisplayRegionTargetStatus(node.id, targetNodeStatus)) {
                    continue;
                }

                const nodeStatus = this._displayRegionDataStatus?.[node.id]?.[0] ?? 0;
                if (nodeStatus != targetNodeStatus
                    && this._displayRegionDataProcessedStatus[node.id] == targetNodeStatus) {
                    this._displayRegionDataStatus[node.id][0] = targetNodeStatus;
                    continue;
                }

                if (nodeStatus == targetNodeStatus || nodeStatus == targetNodeStatus - 1) {
                    continue;
                }

                const fileId = this._displayRegionDataCache[node.id]?.[targetLodLevel];
                if (fileId == null) {
                    continue;
                }

                if (pageRequests.length === 0) {
                    batchLoadType = normalizedBatchLoadType;
                    if (batchLoadType === "point cloud") {
                        batchSize = Math.max(1, Math.floor(this._pointCloudPageRequestBatchSize || 1));
                    }
                }

                pageRequests.push({
                    BlockId: node.id,
                    PageId: "0",
                    DisplayListId: fileId,
                    MetadataFileId: this._displayRegionMetadataDataCache[node.id]?.[targetLodLevel],
                    LoadDataType: loadType,
                    LOD: targetNodeStatus,
                    ContentDisplayPriority: i
                });
            }

            if (pageRequests.length <= 1) {
                return false;
            }

            // Commit request state only after we know this is a real batch. When there is
            // only one candidate, the caller falls back to the individual request path.
            // Mutating state before that decision would reserve memory and schedule it twice.
            for (const pageRequest of pageRequests) {
                if (this.showRegionBoxes) {
                    this._regionMeshes[Number(pageRequest.BlockId)]?.setEnabled(true);
                    this._pointMeshes[Number(pageRequest.BlockId)]?.setEnabled(true);
                }

                this._regionDataLength += this.getRegionMemory(pageRequest.BlockId, pageRequest.LOD / 2);
                this._displayRegionDataStatus[pageRequest.BlockId][pageRequest.PageId] = pageRequest.LOD - 1;

                if (this._tasksForCompletePage[pageRequest.BlockId] == null) {
                    this._tasksForCompletePage[pageRequest.BlockId] = {};
                }

                this._tasksForCompletePage[pageRequest.BlockId][pageRequest.PageId] = 1;
            }

            const firstPageRequest = pageRequests[0];
            const data: Data.GraphicData.WebWorkerDataRequest = {
                BlockId: firstPageRequest.BlockId,
                PageId: firstPageRequest.PageId,
                DisplayListId: firstPageRequest.DisplayListId,
                MetadataFileId: firstPageRequest.MetadataFileId,
                PageRequests: pageRequests,
                ProjectId: DisplayWorker.ProjectID,
                Url: DisplayWorker.DisplayRequestURL,
                DecoderUrl: DisplayWorker.DisplayDecoderURL,
                OctreeUrl: DisplayWorker.OctreeRequestURL,
                ActivatedNamedLists: DisplayWorker.ActivatedNamedLists,
                AllNamedLists: DisplayWorker.AllNamedLists,
                UseDefaultMaterial: DisplayWorker.UseDefaultMaterial,
                UseXRayMaterial: DisplayManager.APPLY_XRAY_IN_WORKER_PARSE,
                WebWorkerNumber: 0,
                DisplayGeneration: this._displayRequestGeneration,
                AccessToken: authContext.accessToken,
                LoadDataType: firstPageRequest.LoadDataType || batchLoadType,
                LOD: firstPageRequest.LOD || 0,
                Type: 2,
                ContentDisplayPriority: firstPageRequest.ContentDisplayPriority,
                AuthContext: authContext,
            };

            this._pool.addTask(data);
            return true;
        }
        catch (error) {

            console.log("Display Error " + error);
            return false;
        }
    }

    public processDisplayRegionOctantContent(displayParameters: any, blockId: string, pageId: string, priority: number, level:number, loadType:string) {

        try {
            const authContext = getCurrentWorkerAuthContext();
            const lodIndex = level / 2;

            var data: Data.GraphicData.WebWorkerDataRequest = {

                BlockId: blockId,
                PageId: pageId,
                DisplayListId: displayParameters,
                MetadataFileId: this._displayRegionMetadataDataCache[blockId]?.[lodIndex],
                ProjectId: DisplayWorker.ProjectID,
                Url: DisplayWorker.DisplayRequestURL,
                DecoderUrl: DisplayWorker.DisplayDecoderURL,
                OctreeUrl: DisplayWorker.OctreeRequestURL,
                ActivatedNamedLists: DisplayWorker.ActivatedNamedLists,
                AllNamedLists: DisplayWorker.AllNamedLists,
                UseDefaultMaterial: DisplayWorker.UseDefaultMaterial,
                UseXRayMaterial: DisplayManager.APPLY_XRAY_IN_WORKER_PARSE,
                WebWorkerNumber: 0,
                DisplayGeneration: this._displayRequestGeneration,
                AccessToken: authContext.accessToken,
                LoadDataType: loadType,
                LOD: level,
                Type: 2,
                ContentDisplayPriority: priority,
                AuthContext: authContext,
            };

            var primCount = 0;

            var startSplitIndex = 0;

            if (this._tasksForCompletePage[blockId] == null) {
                this._tasksForCompletePage[blockId] = {};
            }

            this._tasksForCompletePage[blockId][pageId] = 1;
            this._pool.addTask(data);
        }
        catch (error) {

            console.log("Display Error " + error);
        }
    }

    public processDisplayData(displayParameters: any) {

        Pool.testThreading = false;

        if (this._pool == null) {


            var that = this;
            var cb = function (workerNumber: number, event: MessageEvent) {

                that._processedWorkerEvents.push(event);
            };

            var numThreads = (<any>navigator).hardwareConcurrency || 4;
            this._pool = new Pool(numThreads, false, cb);
        }

        try {
            var data = new Data.GraphicData.GraphicalDataResults(displayParameters);


            var dataLength = data.DataLength;
            var primCount = 0;

            if (this._tasksForCompletePage[0] == null) {
                this._tasksForCompletePage[0] = {};
            }

            this._tasksForCompletePage[0][0] = 1;

            if (this._displayDataStatus[0] == null) {
                this._displayDataStatus[0] = {};
            }

            this._displayDataStatus[0][0] = 0;

            var startSplitIndex = 0;
            for (var z = 0; z < dataLength; z++) {

                if (data.GraphicDataResult(z).Primitives != null) {
                    primCount += data.GraphicDataResult(z).Primitives.length;

                    if ((z + 1) == dataLength || primCount > 500) {

                        primCount = 0;

                        var slice = new Data.GraphicData.GraphicalDataResults({
                            GraphicObjects: displayParameters.GraphicObjects.slice(startSplitIndex, z + 1),
                            AppearanceOverrideLists: displayParameters.AppearanceOverrideLists
                        });

                        var taskData: Data.GraphicData.WebWorkerGraphicData = {
                            GraphicData: slice.data,
                            BlockId: "0",
                            PageId: "0",
                            WebWorkerNumber: 0,
                            DisplayGeneration: this._displayRequestGeneration,
                            LoadDataType: "",
                            Type: 3,
                            ContentDisplayPriority: 1,
                            UseDefaultMaterial: DisplayWorker.UseDefaultMaterial,
                            UseXRayMaterial: DisplayManager.APPLY_XRAY_IN_WORKER_PARSE
                        };

                        this._pool.addTask(taskData);

                        startSplitIndex = z + 1;
                    }
                }
            }
        }
        catch (error) {

            console.log("Display Error " + error);
        }
    }

    public isDisplaying(): boolean {
        return this._displaying;
    }
    public changeAppearanceOverrides(namedLists: any[], activateDefaultList: boolean) {

        var selectedObjects = this._selectionManager.getSelectedAssetIds();
        var hiddenObjects = this._meshManager.getHiddenAssetIds();
        this._selectionManager.unselectAllAssets(false);
        this._meshManager.unhideAllAssets(false);
        this.abandonCurrentWorkerResults();
        this._meshManager.changeAppearanceOverrides(namedLists, activateDefaultList, DisplayWorker.UseXRayMaterial, this._overrideManager);
        this._meshManager.setRegionXRayMode(DisplayWorker.UseXRayMaterial);
        DisplayWorker.ActivatedNamedLists = namedLists;
        DisplayWorker.UseDefaultMaterial = activateDefaultList;

        this._selectionManager.selectObjects(selectedObjects, false, Enumerations.PickObjectType.Unknown, false);
        this._meshManager.hideObjects(hiddenObjects, false);
    }

    public displayLabels(labelData: any[]) {
        for (var i = 0; i < labelData.length; i++) {
            this._annotationManager.processLabelData(labelData[i]);
        }
    }

    public applyKnowledgeViewAssetSetsAndRefresh(
        assetSets: {
            id: string;
            priority: number;
            colorKey?: number;
            hidden?: boolean;
            isAll3DAssets?: boolean;
            materialID?: string | null;
        }[],
        strictIsolation: boolean = false
    ): void {

        // 1) Route everything through AssetSetManager (authoritative state)
        this._assetSetManager.applyKnowledgeViewAssetSets((assetSets || []).map(assetSet => ({
            ID: assetSet.id,
            Ord: assetSet.priority,
            Color: assetSet.colorKey,
            Hidden: assetSet.hidden,
            MaterialID: assetSet.materialID,
        })));
        this._assetSetManager.syncAssetSetVisibility(strictIsolation, false);

        // Drive CAD residency from the actual All Assets display entry. This
        // avoids depending on membership/ownership publication timing for a
        // presentation-only Hidden transition.
        const allAssetsSetId = this._assetSetManager.getAllSetID();
        const normalizedAllAssetsSetId = allAssetsSetId?.toLocaleLowerCase?.();
        const allAssetsEntry = (assetSets || []).find(assetSet =>
            !!assetSet.isAll3DAssets ||
            (!!normalizedAllAssetsSetId && assetSet.id?.toLocaleLowerCase?.() === normalizedAllAssetsSetId));
        if (allAssetsEntry) {
            this._setAllAssetsCadGeometryEnabled(
                allAssetsEntry.priority > 0 && !allAssetsEntry.hidden
            );
        } else if (strictIsolation) {
            this._setAllAssetsCadGeometryEnabled(false);
        }
        this.refreshAssetGeometryOwnership();

        // 2) Unload any currently-loaded asset pages that belong to now-disabled asset sets
        this._unloadDisabledAssetSetPages();

        // 3) Force renderer to reconsider visibility
        this._meshManager.meshesDirty = true;
    }

    public refreshAssetGeometryOwnership(): void {
        const ownership = this._assetSetManager.refreshGeometryOwnership();
        this._assetGeometryOwnership = ownership;
        this._pool?.setAssetGeometryOwnership(ownership);

        this._setAllAssetsCadGeometryEnabled(!ownership.defaultUnownedGeometryHidden);

        if (ownership.changedAssetSetIds.length > 0) {
            this._reconcileAssetGeometryOwnership(new Set(ownership.changedAssetSetIds));
        }
    }

    private _disposeTrackedAssetPage(
        blockId: string,
        pageId: string,
        trackedLength?: number,
        disposedObjectIds?: any[][],
        deferAppearanceCleanup: boolean = false
    ): void {
        const pageKey = `${blockId}_${pageId}`;

        if (!disposedObjectIds) {
            disposedObjectIds = this._meshManager.getObjectIdsByBlockPage(blockId, pageId);
        }

        this._selectionManager.setObjectsToMaintainSelection(disposedObjectIds);
        this._meshManager.setObjectsToMaintainHidden(disposedObjectIds);
        this._meshManager.disposePageData(blockId, pageId);

        if (!deferAppearanceCleanup) {
            this._overrideManager.forgetAssets(disposedObjectIds);
        }

        const disposeSize = trackedLength ?? this._pageLength[pageKey] ?? 0;
        this._pageLength[pageKey] = 0;
        this._dataLength = Math.max(0, this._dataLength - disposeSize);
        delete this._filteredAssetIdsByPage[pageKey];
    }

    private _reconcileAssetGeometryOwnership(changedSourceSetIds: Set<string>): void {
        if (!this._displayDataStatus) return;

        const disposedAssetIds = new Set<string>();
        this._meshManager.beginCylinderInstanceDisposalBatch();
        try {
            for (const blockId in this._displayDataStatus) {
                const pages = this._displayDataStatus[blockId];
                for (const pageId in pages) {
                    const pageKey = `${blockId}_${pageId}`;
                    const hasTrackedGeometry = pages[pageId] === 2 || (this._pageLength[pageKey] || 0) > 0;
                    if (!hasTrackedGeometry) continue;

                    const sourceAssetSetId = this._assetLayerByNodeKey[pageKey];
                    if (!sourceAssetSetId || !changedSourceSetIds.has(sourceAssetSetId)) continue;

                    const pageObjectInfos = this._meshManager.getObjectIdsByBlockPage(blockId, pageId);
                    const processedAssetIds = Array.from(new Set(
                        pageObjectInfos.flatMap(objectInfos =>
                            (objectInfos || []).map((objectInfo: any) => objectInfo?.objectId).filter(Boolean))
                    ));
                    const filteredAssetIds = this._filteredAssetIdsByPage[pageKey] || [];
                    const hasDisallowedGeometry = processedAssetIds.some(assetId =>
                        !this._assetSetManager.isAssetGeometryAllowed(assetId, sourceAssetSetId));
                    const hasNewlyAllowedGeometry = filteredAssetIds.some(assetId =>
                        this._assetSetManager.isAssetGeometryAllowed(assetId, sourceAssetSetId));

                    if (!hasDisallowedGeometry && !hasNewlyAllowedGeometry) continue;

                    for (let i = 0; i < processedAssetIds.length; i++) {
                        disposedAssetIds.add(processedAssetIds[i]);
                    }
                    this._disposeTrackedAssetPage(blockId, pageId, undefined, pageObjectInfos, true);
                    pages[pageId] = 0;
                }
            }
        } finally {
            this._meshManager.endCylinderInstanceDisposalBatch();
            this._overrideManager.forgetAssets([Array.from(disposedAssetIds)]);
        }

        this._meshManager.meshesDirty = true;
    }

    private _unloadDisabledAssetSetPages(): void {
        if (this._displayDataStatus) {
            this._meshManager.beginCylinderInstanceDisposalBatch();
            try {
                for (const blockId in this._displayDataStatus) {
                    const pages = this._displayDataStatus[blockId];
                    if (!pages) continue;

                    for (const pageId in pages) {
                        const nodeKey = `${blockId}_${pageId}`;
                        const assetSetId = this._assetLayerByNodeKey[nodeKey];

                        // If this page belongs to an asset set that is no longer enabled, unload it
                        if (assetSetId && !this._assetSetManager.isSetGeometryEnabled(assetSetId)) {
                            const hasTrackedGeometry = pages[pageId] === 2 || (this._pageLength[nodeKey] || 0) > 0;
                            if (hasTrackedGeometry) {
                                this._disposeTrackedAssetPage(blockId, pageId);
                            }

                            this._displayDataStatus[blockId][pageId] = 0;
                            if (DebugOptions.valueOf()) {
                                this._meshManager.setBoxLoaded(blockId, -1);
                            }
                        }
                    }
                }
            }
            finally {
                this._meshManager.endCylinderInstanceDisposalBatch();
            }
        }
    }

    public setContentLayers(contentLayers: ContentLayers) {
        const prev = this._contentLayers;
        this._contentLayers = { ...contentLayers };

        // If assets got disabled, unload all “standard” pages (octree/mesh pages)
        if (prev.assets && !contentLayers.assets) {
            this._unloadAllAssetPages();
        }

        // If CAD or Point Cloud got disabled, unload any already displayed regions of those types
        if ((prev.cad && !contentLayers.cad) || (prev.pointCloud && !contentLayers.pointCloud) || (prev.mesh && !contentLayers.mesh)) {
            this._unloadRegionsByType((type) => {
                const t = type?.toLowerCase?.() ?? "";
                const isMeshLike = t === "mesh" ;
                const isGLBLike = t === "glb" || t === "pagedglb" || t === "pagedfbx";
                const isPointCloud = t === "point cloud" || t === "pointcloud";
                return (isMeshLike && !contentLayers.mesh) || (isGLBLike && !contentLayers.cad) || (isPointCloud && !contentLayers.pointCloud);
            });
        }

        // Asset tags (annotations) visibility — best effort depending on your annotation manager API
        if (!contentLayers.assetTags) {
            this._labelManager.setTagsVisible(false);
        } else {
            this._labelManager.setTagsVisible(true);
        }

        if (!contentLayers.assemblies) {
            this._assemblyManager.setAssembliesVisible(false);

        }
        else {
            this._assemblyManager.setAssembliesVisible(true);
        }

        const showScanPositions = contentLayers.positions360;
        this._scanPositionManager.setColorScanPositionsVisible(showScanPositions && contentLayers.colorScanPositions);
        this._scanPositionManager.setBlackAndWhiteScanPositionsVisible(showScanPositions && contentLayers.blackAndWhiteScanPositions);

        if (!contentLayers.positions360 || !contentLayers.imagePositions) {
            this._scanPositionManager.setImagePositionsVisible(false);
        } else {
            this._scanPositionManager.setImagePositionsVisible(true);
        }

        const assetLayerReenabled = !prev.assets && contentLayers.assets;
        const regionLayerReenabled = (!prev.cad && contentLayers.cad)
            || (!prev.pointCloud && contentLayers.pointCloud)
            || (!prev.mesh && contentLayers.mesh);

        if (assetLayerReenabled && this._visibleNodes?.length) {
            this.processMemoryPressureDisplay(this, this._visibleNodes, this._hiddenNodes ?? []);
        }

        if (regionLayerReenabled && this._visibleRegionNodes.length) {
            this.processRegionMemoryPressureDisplay(this, this._visibleRegionNodes, this._hiddenRegionNodes);
        }

        // Nudge renderer to reconsider what’s visible now
        this._meshManager.meshesDirty = true;
    }

    private _unloadAllAssetPages() {

        const pagesToUnload: { blockId: string; pageId: string; disposedObjectIds: any[]; disposeSize: number; }[] = [];

        for (const blockId in this._displayDataStatus) {
            const pages = this._displayDataStatus[blockId];
            for (const pageId in pages) {
                if (pages[pageId] === 2) { // in scene
                    pagesToUnload.push({
                        blockId: blockId,
                        pageId: pageId,
                        disposedObjectIds: this._meshManager.getObjectIdsByBlockPage(blockId, pageId),
                        disposeSize: this._pageLength[blockId + "_" + pageId] || 0
                    });
                }
            }
        }

        if (pagesToUnload.length === 0) {
            return;
        }

        this._meshManager.disposeAllCylinderInstanceMasters();

        for (let i = 0; i < pagesToUnload.length; i++) {
            const page = pagesToUnload[i];

            this._selectionManager.setObjectsToMaintainSelection(page.disposedObjectIds);
            this._meshManager.setObjectsToMaintainHidden(page.disposedObjectIds);

            this._meshManager.disposePageData(page.blockId, page.pageId);
            this._overrideManager.forgetAssets(page.disposedObjectIds);

            this._pageLength[page.blockId + "_" + page.pageId] = 0;
            this._dataLength -= page.disposeSize;

            this._displayDataStatus[page.blockId][page.pageId] = 0;
            if (DebugOptions.valueOf()) this._meshManager.setBoxLoaded(page.blockId, -1);
        }
    }

    private _unloadRegionsByType(predicate: (type: string) => boolean) {
        const regionIds = new Set([
            ...Object.keys(this._displayRegionDataProcessedStatus || {}),
            ...Object.keys(this._displayRegionDataStatus || {}),
        ]);
        for (const regionId of regionIds) {
            const processedStatus = this._displayRegionDataProcessedStatus[regionId] ?? 0;
            const currentStatus = this._displayRegionDataStatus?.[regionId]?.[0] ?? 0;
            const statuses = processedStatus > 0
                ? [processedStatus, currentStatus]
                : [currentStatus];
            const type = statuses
                .filter((status, index) => status > 0 && statuses.indexOf(status) === index)
                .map(status => this._displayRegionDataTypes[regionId]?.[Math.ceil(status / 2)])
                .find(candidate => !!candidate);

            if (type && predicate(type)) {
                this.unloadRegion(regionId);
            }
        }
    }

    private _isCadRegionType(type: string): boolean {
        const normalizedType = type?.toLowerCase?.() ?? "";
        return normalizedType === "glb"
            || normalizedType === "pagedglb"
            || normalizedType === "pagedfbx";
    }

    private _canLoadCadRegions(): boolean {
        return this._contentLayers.cad && this._allAssetsCadGeometryEnabled;
    }

    private _setAllAssetsCadGeometryEnabled(enabled: boolean): void {
        if (this._allAssetsCadGeometryEnabled === enabled) {
            return;
        }

        this._allAssetsCadGeometryEnabled = enabled;

        if (!enabled) {
            this._unloadRegionsByType(type => this._isCadRegionType(type));
            this.fireCombinedDisplayProgress();
            return;
        }

        if (this._contentLayers.cad && this._visibleRegionNodes.length > 0) {
            this.processRegionMemoryPressureDisplay(
                this,
                this._visibleRegionNodes,
                this._hiddenRegionNodes
            );
        }
    }

    public subWorkersSupported(): boolean {
        return false;
    }

    public hasPendingDisplayData(): boolean {
        if (this._processedWorkerEvents == null)
            return false;

        return this._processedWorkerEvents.length > 0;
    }

    private _releaseWorkerForEvent(event: any): void {
        if (event?.data?.workerTaskComplete === false) {
            return;
        }

        if (!this.subWorkersSupported() && this._pool != null && event?.data?.workerNumber != null) {
            this._pool.makeWorkerAvailable(event.data.workerNumber);
        }
    }

    private _getEventDisplayGeneration(event: any): number {
        if (event?.data?.displayGeneration != null) {
            return event.data.displayGeneration;
        }

        if (event?.data?.DisplayGeneration != null) {
            return event.data.DisplayGeneration;
        }

        return 0;
    }

    private _hasTrackedTask(blockId: string, pageId: string): boolean {
        return this._tasksForCompletePage != null
            && this._tasksForCompletePage[blockId] != null
            && this._tasksForCompletePage[blockId][pageId] != null;
    }

    private _hasTrackedAssetPage(blockId: string, pageId: string): boolean {
        return this._displayDataStatus != null
            && this._displayDataStatus[blockId] != null
            && this._displayDataStatus[blockId][pageId] != null;
    }

    private _hasTrackedRegionPage(blockId: string, pageId: string): boolean {
        return this._displayRegionDataStatus != null
            && this._displayRegionDataStatus[blockId] != null
            && this._displayRegionDataStatus[blockId][pageId] != null;
    }

    private _discardDisabledRegionResult(event: any): void {
        const blockId = event?.data?.blockId;
        const pageId = event?.data?.pageId;
        if (blockId == null || pageId == null) {
            return;
        }

        if (this._hasTrackedTask(blockId, pageId)) {
            this._tasksForCompletePage[blockId][pageId] = 0;
        }

        // A rejected region result must not leave the page marked as loading.
        // Otherwise a later layer re-enable sees LOD - 1 and never queues it again.
        if (this._hasTrackedRegionPage(blockId, pageId)
            && this._displayRegionDataStatus[blockId][pageId] === event.data.lod - 1) {
            this._displayRegionDataStatus[blockId][pageId] = 0;
        }
    }

    private _isAssetWorkerEvent(event: any): boolean {
        const dataType = event?.data?.dataType;
        if (dataType === 1) {
            return true;
        }

        return (dataType || "").toString().toLowerCase() === "assets";
    }

    private _isAssetLayerEnabledForPage(blockId: string, pageId: string): boolean {
        const nodeKey = `${blockId}_${pageId}`;
        const assetLayerId = this._assetLayerByNodeKey[nodeKey];
        return assetLayerId == null || this._assetSetManager.isSetGeometryEnabled(assetLayerId);
    }

    private _discardDisabledAssetPageResult(event: any): boolean {
        if (!this._isAssetWorkerEvent(event)) return false;

        const blockId = event?.data?.blockId;
        const pageId = event?.data?.pageId;
        if (blockId == null || pageId == null) return false;
        if (this._isAssetLayerEnabledForPage(blockId, pageId)) return false;

        if (this._tasksForCompletePage?.[blockId]?.[pageId] != null) {
            this._tasksForCompletePage[blockId][pageId] = 0;
        }

        const pageKey = `${blockId}_${pageId}`;
        if (this._displayDataStatus?.[blockId]?.[pageId] === 2 || (this._pageLength[pageKey] || 0) > 0) {
            this._disposeTrackedAssetPage(blockId, pageId);
        }

        if (this._displayDataStatus?.[blockId]?.[pageId] != null) {
            this._displayDataStatus[blockId][pageId] = 0;
        }

        return true;
    }

    private _discardStaleAssetGeometryResult(event: any): boolean {
        if (!this._isAssetWorkerEvent(event)) return false;

        const blockId = event?.data?.blockId;
        const pageId = event?.data?.pageId;
        if (blockId == null || pageId == null) return false;

        const sourceAssetSetId = event?.data?.sourceAssetSetId
            || this._assetLayerByNodeKey[`${blockId}_${pageId}`];
        if (!sourceAssetSetId) return false;

        const resultEpoch = event?.data?.geometryOwnershipEpoch;
        if (resultEpoch == null ||
            resultEpoch === this._assetSetManager.getGeometryOwnershipEpoch(sourceAssetSetId)) {
            return false;
        }

        if (this._tasksForCompletePage?.[blockId]?.[pageId] != null) {
            this._tasksForCompletePage[blockId][pageId] = 0;
        }
        if (this._displayDataStatus?.[blockId]?.[pageId] != null) {
            this._displayDataStatus[blockId][pageId] = 0;
        }
        return true;
    }

    private _rememberAssetGeometryPageResult(event: any): void {
        if (!this._isAssetWorkerEvent(event)) return;
        const blockId = event?.data?.blockId;
        const pageId = event?.data?.pageId;
        if (blockId == null || pageId == null) return;

        const pageKey = `${blockId}_${pageId}`;
        this._filteredAssetIdsByPage[pageKey] = Array.from(new Set(event.data.filteredObjectIds || []));
    }

    private _shouldIgnoreWorkerEvent(event: any): boolean {
        const blockId = event?.data?.blockId;
        const pageId = event?.data?.pageId;

        if (blockId == null || pageId == null) {
            return true;
        }

        if (this._getEventDisplayGeneration(event) !== this._displayRequestGeneration) {
            return true;
        }

        if (!this._hasTrackedTask(blockId, pageId)) {
            return true;
        }

        if (this._isAssetWorkerEvent(event)) {
            return !this._hasTrackedAssetPage(blockId, pageId);
        }

        return !this._hasTrackedRegionPage(blockId, pageId);
    }

    private _batchOverrideMaintenance(callback: () => void): void {
        this._overrideManager.beginBatch();
        try {
            callback();
        }
        finally {
            this._overrideManager.endBatch();
        }
    }

    private _processInstanceContents(instanceContents: any[], blockId: string, pageId: string, batchOverrideMaintenance: boolean = true): number {
        if (!instanceContents || instanceContents.length === 0) {
            return 0;
        }

        const instanceResult = this._meshManager.processInstancePayload(instanceContents, blockId, pageId);
        const instanceLength = instanceResult.memoryLength;
        const baseMaterialByObjectId: { [objectId: string]: string; } = {};

        for (let i = 0; i < instanceContents.length; i++) {
            const instanceContent = instanceContents[i];
            if (!instanceContent || !instanceContent.ObjectId || !instanceContent.MaterialKey) {
                continue;
            }

            baseMaterialByObjectId[instanceContent.ObjectId] = instanceContent.MaterialKey;
        }

        if (instanceLength > 0) {
            this._pageLength[blockId + "_" + pageId] = (this._pageLength[blockId + "_" + pageId] || 0) + instanceLength;
            this._dataLength += instanceLength;
        }

        const maintainInstances = () => {
            for (let i = 0; i < instanceResult.objectIds.length; i++) {
                const objectId = instanceResult.objectIds[i];
                this._meshManager.maintainHidden(objectId);
                const instanceBaseMaterial = baseMaterialByObjectId[objectId];
                const instanceBaseMaterials = instanceBaseMaterial ? [instanceBaseMaterial] : this._meshManager.getOverrideMaterialOfObject(objectId);
                this._overrideManager.maintainAsset(objectId, instanceBaseMaterials);
                this._assetSetManager.maintainAssetSet(objectId);
                this._selectionManager.maintainSelection(objectId, Enumerations.PickObjectType.Unknown);
            }
        };

        if (batchOverrideMaintenance) {
            this._batchOverrideMaintenance(maintainInstances);
        }
        else {
            maintainInstances();
        }

        return instanceLength;
    }

    public processPendingDisplayData(numberToProcess: number): number {
        var numberOfProcessedData = 0;

        while ((this._processedWorkerEvents.length > 0) && (numberOfProcessedData < numberToProcess)) {

            var event = this._processedWorkerEvents.shift();

            if (this._shouldIgnoreWorkerEvent(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            var meshContents = event.data.payload;
            var instanceContents = event.data.instancePayload || [];
            var annotations = event.data.annotations;

            const dt = (event.data.dataType || "").toString().toLowerCase();

            // Standard “assets” (your code uses 1 for this path)
            if (!this._contentLayers.assets && event.data.dataType === 1) {
                // mark task not applied; free worker
                this._tasksForCompletePage[event.data.blockId][event.data.pageId] = 0;
                this._displayDataStatus[event.data.blockId][event.data.pageId] = 0;
                this._releaseWorkerForEvent(event);
                continue;
            }

            if (this._discardDisabledAssetPageResult(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            if (this._discardStaleAssetGeometryResult(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            this._rememberAssetGeometryPageResult(event);

            if ((dt === "mesh") && !this._contentLayers.mesh) {
                this._discardDisabledRegionResult(event);
                this._releaseWorkerForEvent(event);
                continue;
            }

            // Region: CAD / Point Cloud results
            if (this._isCadRegionType(dt) && !this._canLoadCadRegions()) {
                this._discardDisabledRegionResult(event);
                this._releaseWorkerForEvent(event);
                continue;
            }
            if ((dt === "point cloud" || dt === "pointcloud") && !this._contentLayers.pointCloud) {
                this._discardDisabledRegionResult(event);
                this._releaseWorkerForEvent(event);
                continue;
            }

            if (event.data.emptyResultReason) {
                this._tasksForCompletePage[event.data.blockId][event.data.pageId] = 0;

                if (this._hasTrackedRegionPage(event.data.blockId, event.data.pageId)) {
                    this._displayRegionDataStatus[event.data.blockId][event.data.pageId] = 0;
                }

                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            this._tasksForCompletePage[event.data.blockId][event.data.pageId]--;

            if (event.data.dataType == 1) {

                if (this._displayDataStatus[event.data.blockId][event.data.pageId] == -1) {

                    this._displayDataStatus[event.data.blockId][event.data.pageId] = 0;
                }
                else {
                    if (DebugOptions.valueOf()) {
                        this._meshManager.setBoxLoaded(event.data.blockId, 1);
                    }

                    if (this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {
                        this._displayDataStatus[event.data.blockId][event.data.pageId] = 2;
                    }

                    this._batchOverrideMaintenance(() => {
                        for (var i = 0; i < meshContents.length; i++) {

                            // make sure the display layer is still on for this content
                            const nodeKey = `${event.data.blockId}_${event.data.pageId}`;
                            const assetLayerId = this._assetLayerByNodeKey[nodeKey];

                            if (assetLayerId != null && !this._assetSetManager.isSetGeometryEnabled(assetLayerId)) {
                                continue; // skip loading for this node
                            }

                            var geoLength = this._meshManager.processMeshData(meshContents[i], event.data.objectBaseMaterialMap, event.data.appearanceOverrides, event.data.blockId, event.data.pageId);
                            //var geoLength = 0;
                            if (this._pageLength[event.data.blockId + "_" + event.data.pageId] == null) {
                                this._pageLength[event.data.blockId + "_" + event.data.pageId] = geoLength;
                            }
                            else {
                                this._pageLength[event.data.blockId + "_" + event.data.pageId] += geoLength;
                            }

                            for (var x = 0; x < meshContents[i].IdToMaster.length; x++) {

                                //this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x]);
                                this._meshManager.maintainHidden(meshContents[i].IdToMaster[x]);
                                var baseMaterial = this._meshManager.getOverrideMaterialOfObject(meshContents[i].IdToMaster[x]);
                                // Update override Manager with this asset's base material
                                this._overrideManager.maintainAsset(meshContents[i].IdToMaster[x], baseMaterial);
                                this._assetSetManager.maintainAssetSet(meshContents[i].IdToMaster[x]);
                                this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x], Enumerations.PickObjectType.Unknown);
                            }

                            this._dataLength += geoLength;
                        }

                        this._processInstanceContents(instanceContents, event.data.blockId, event.data.pageId, false);
                    });

                    this._meshManager.processMeshDataMaterialMap(event.data.objectBaseMaterialMap);

                    if (annotations.markedPoints != null) {
                        for (var i = 0; i < annotations.markedPoints.length; i++) {
                            this._annotationManager.processMarkedPointData(annotations.markedPoints[i], event.data.blockId, event.data.pageId);
                        }
                    }

                    this._meshManager.meshesDirty = true;
                }
            }
            else if (event.data.dataType >= 2) {

                {

                    if (this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {

                        this._displayRegionDataStatus[event.data.blockId][event.data.pageId] = event.data.dataType;

                        // if all pages in the block are meshed, set the 0 page "the region status" to done as well.

                        if (event.data.dataType == 12) {
                            var allDone = true;

                            for (var x = 1; x < this._regionSegmentCount[event.data.blockId]; x++) {
                                if (this._displayRegionDataStatus[event.data.blockId][x] != event.data.dataType)
                                    allDone = false;
                            }

                            if (allDone) {
                                this._displayRegionDataStatus[event.data.blockId][0] = event.data.dataType;
                            }
                        }
                    }

                    for (var i = 0; i < meshContents.length; i++) {

                        var geoLength = this._meshManager.processRegionMeshData(
                            meshContents[i],
                            event.data.dataType,
                            event.data.blockId,
                            event.data.pageId,
                            this._voxelMMByIndex[(event.data.dataType / 2)],
                            typeof event.data.lod === "number" ? (event.data.lod / 2) : undefined
                        );

                        if (this._pageLength[event.data.blockId + "_" + event.data.pageId] == null) {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] = geoLength;
                        }
                        else {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] += geoLength;
                        }

                        this._batchOverrideMaintenance(() => {
                            for (var x = 0; x < meshContents[i].IdToMaster.length; x++) {

                                //this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x]);
                                this._meshManager.maintainHidden(meshContents[i].IdToMaster[x]);
                                var baseMaterial = this._meshManager.getOverrideMaterialOfObject(meshContents[i].IdToMaster[x]);
                                var mastermeshes = this._meshManager.getMasterKeysOfObject(meshContents[i].IdToMaster[x]);
                                // Update override Manager with this asset's base material
                                this._overrideManager.maintainAsset(meshContents[i].IdToMaster[x], baseMaterial);
                                this._assetSetManager.maintainAssetSet(meshContents[i].IdToMaster[x]);
                                this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x], Enumerations.PickObjectType.GLBObject);
                            }
                        });
                    }

                    if (this._displayRegionDataStatus[event.data.blockId][0] == event.data.dataType) {
                        this.reconcileTrackedRegionMemory(event.data.blockId, event.data.dataType / 2);
                    }

                    //this._meshManager.processMeshDataMaterialMap(event.data.objectBaseMaterialMap);
                    /*
                    if (annotations.markedPoints != null) {
                        for (var i = 0; i < annotations.markedPoints.length; i++) {
                            this._annotationManager.processMarkedPointData(annotations.markedPoints[i], event.data.blockId, event.data.pageId);
                        }
                    }
                    */
                    this._meshManager.meshesDirty = true;
                }
            }
            else if (event.data.dataType.toLowerCase != null && event.data.dataType.toLowerCase() == "assets") {

                //this._tasksForCompletePage[event.data.blockId][event.data.pageId]--;

                if (this._displayDataStatus[event.data.blockId][event.data.pageId] == -1) {

                    this._displayDataStatus[event.data.blockId][event.data.pageId] = 0;
                }
                else {
                    if (DebugOptions.valueOf()) {
                        this._meshManager.setBoxLoaded(event.data.blockId, 1);
                    }

                    if (this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {
                        this._displayDataStatus[event.data.blockId][event.data.pageId] = 2;
                    }

                    this._batchOverrideMaintenance(() => {
                        for (var i = 0; i < meshContents.length; i++) {

                            // make sure the display layer is still on for this content
                            const nodeKey = `${event.data.blockId}_${event.data.pageId}`;
                            const assetLayerId = this._assetLayerByNodeKey[nodeKey];

                            if (assetLayerId != null && !this._assetSetManager.isSetGeometryEnabled(assetLayerId)) {

                                continue; // skip loading for this node
                            }

                            var geoLength = this._meshManager.processMeshData(meshContents[i], event.data.objectBaseMaterialMap, event.data.appearanceOverrides, event.data.blockId, event.data.pageId);

                            if (this._pageLength[event.data.blockId + "_" + event.data.pageId] == null) {
                                this._pageLength[event.data.blockId + "_" + event.data.pageId] = geoLength;
                            }
                            else {
                                this._pageLength[event.data.blockId + "_" + event.data.pageId] += geoLength;
                            }

                            for (var x = 0; x < meshContents[i].IdToMaster.length; x++) {

                                //this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x]);
                                this._meshManager.maintainHidden(meshContents[i].IdToMaster[x]);

                                var baseMaterial = this._meshManager.getOverrideMaterialOfObject(meshContents[i].IdToMaster[x]);
                                // Update override Manager with this asset's base material
                                this._overrideManager.maintainAsset(meshContents[i].IdToMaster[x], baseMaterial);
                                this._assetSetManager.maintainAssetSet(meshContents[i].IdToMaster[x]);
                                this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x], Enumerations.PickObjectType.Unknown);
                            }

                            this._dataLength += geoLength;
                        }

                        this._processInstanceContents(instanceContents, event.data.blockId, event.data.pageId, false);
                    });

                    this._meshManager.processMeshDataMaterialMap(event.data.objectBaseMaterialMap);

                    if (annotations.markedPoints != null) {
                        for (var i = 0; i < annotations.markedPoints.length; i++) {
                            this._annotationManager.processMarkedPointData(annotations.markedPoints[i], event.data.blockId, event.data.pageId);
                        }
                    }

                    this._meshManager.meshesDirty = true;
                }
            }
            else if (event.data.dataType.toLowerCase != null && this._displayRegionDataStatus[event.data.blockId][event.data.pageId] == event.data.lod - 1 && (event.data.dataType.toLowerCase() == "point cloud" || event.data.dataType.toLowerCase() == "pointcloud" || event.data.dataType.toLowerCase() == "mesh" || event.data.dataType.toLowerCase() == "glb" || event.data.dataType.toLowerCase() == "pagedglb" || event.data.dataType.toLowerCase() == "pagedfbx")) {
                {

                    if (this._displayRegionDataProcessedStatus[event.data.blockId] != event.data.lod) {
                        this.unloadRegion(event.data.blockId);
                        //this._displayRegionDataStatus[event.blockId]["0"] =
                    }

                    if (this._displayRegionDataProcessedStatus[event.data.blockId] == event.data.lod) {
                        // The requested LOD is already resident, but a repeated request may have
                        // left the display status at the loading sentinel (LOD - 1). Reconcile the
                        // public status before completing the duplicate task so progress can finish.
                        if (this._displayRegionDataStatus[event.data.blockId][event.data.pageId] == event.data.lod - 1) {
                            this._displayRegionDataStatus[event.data.blockId][event.data.pageId] = event.data.lod;
                        }
                        this._tasksForCompletePage[event.data.blockId][event.data.pageId] = 0;
                        this._releaseWorkerForEvent(event);
                        continue;
                    }
                    this._displayRegionDataProcessedStatus[event.data.blockId] = event.data.lod;

                    //this._tasksForCompletePage[event.data.blockId][event.data.pageId]--;

                    if (this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {

                        this._displayRegionDataStatus[event.data.blockId][event.data.pageId] = event.data.lod;

                        // if all pages in the block are meshed, set the 0 page "the region status" to done as well.

                        if (event.data.dataType.toLowerCase() == "mesh") {
                            var allDone = true;

                            for (var x = 1; x < this._regionSegmentCount[event.data.blockId]; x++) {
                                if (this._displayRegionDataStatus[event.data.blockId][x] != event.data.lod)
                                    allDone = false;
                            }

                            if (allDone) {
                                this._displayRegionDataStatus[event.data.blockId][0] = event.data.lod;
                            }
                        }
                    }

                    this._meshManager.processRegionMaterials(event.data.materialPayload, event.data.blockId);

                    for (var i = 0; i < meshContents.length; i++) {

                        var geoLength = this._meshManager.processRegionMeshData(
                            meshContents[i],
                            event.data.dataType,
                            event.data.blockId,
                            event.data.pageId,
                            this._voxelMMByIndex[(event.data.lod / 2)],
                            event.data.lod / 2
                        );

                        if (this._pageLength[event.data.blockId + "_" + event.data.pageId] == null) {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] = geoLength;
                        }
                        else {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] += geoLength;
                        }

                        this._batchOverrideMaintenance(() => {
                            for (var x = 0; x < meshContents[i].IdToMaster.length; x++) {


                                this._meshManager.maintainHidden(meshContents[i].IdToMaster[x]);
                                var baseMat = this._meshManager.getOverrideMaterialOfObject(meshContents[i].IdToMaster[x]);
                                const maintainSelectionType = (((meshContents[i] as any).PickObjectType || "").toLowerCase() == Enumerations.PickObjectType.PagedMesh)
                                    ? Enumerations.PickObjectType.PagedMesh
                                    : Enumerations.PickObjectType.GLBObject;
                                // Update override Manager with this asset's base material
                                this._overrideManager.maintainAsset(meshContents[i].IdToMaster[x], baseMat);
                                // Paged region content belongs to the CAD layer, not the facility asset
                                // hierarchy. Applying KV strict asset-set isolation here hides every GLB/FBX
                                // object that does not have asset-set membership.
                                if (dt !== "pagedglb" && dt !== "pagedfbx") {
                                    this._assetSetManager.maintainAssetSet(meshContents[i].IdToMaster[x]);
                                }
                                this._selectionManager.maintainSelection(meshContents[i].IdToMaster[x], maintainSelectionType);
                            }
                        });
                    }

                    if (this._displayRegionDataStatus[event.data.blockId][0] == event.data.lod) {
                        this.reconcileTrackedRegionMemory(event.data.blockId, event.data.lod / 2);
                    }

                    this._meshManager.meshesDirty = true;
                }
            }


            this._releaseWorkerForEvent(event);

            numberOfProcessedData++;
        }

        return numberOfProcessedData;
    }

    public processPendingDisplayDataExperimental(numberToProcess: number): number {
        var numberOfProcessedData = 0;

        while ((this._processedWorkerEvents.length > 0) && (numberOfProcessedData < numberToProcess)) {


            var event = this._processedWorkerEvents.shift();

            if (this._shouldIgnoreWorkerEvent(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            var meshContents = event.data.payload;
            var instanceContents = event.data.instancePayload || [];
            var annotations = event.data.annotations;

            if (this._discardDisabledAssetPageResult(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            if (this._discardStaleAssetGeometryResult(event)) {
                this._releaseWorkerForEvent(event);
                numberOfProcessedData++;
                continue;
            }

            this._rememberAssetGeometryPageResult(event);

            var memoryUsed = this._meshManager.getMemoryConsumptionForData(meshContents, event.data.blockId, event.data.pageId);

            if (this._dataLength + memoryUsed > this.getEffectiveAssetMemoryLimit()) {

                if (!this._pool.isHighestPriorityInProgressWorker(event.data.workerNumber)) {

                    // Stuff it back on the stack, and wait for the highest priority worker to complete.
                    this._processedWorkerEvents.push(event);
                    return;
                }
            }

            this._tasksForCompletePage[event.data.blockId][event.data.pageId]--;

            if (this._displayDataStatus[event.data.blockId][event.data.pageId] == -1) {

                this._displayDataStatus[event.data.blockId][event.data.pageId] = 0;
            }
            else {
                if (DebugOptions.valueOf()) {
                    this._meshManager.setBoxLoaded(event.data.blockId, 1);
                }

                var allowedToLoad = this.freeLowPriorityMemory(
                    memoryUsed,
                    event.data.blockId,
                    event.data.pageId,
                    event.data.contentDisplayPriority ?? event.data.ContentDisplayPriority
                );

                if (allowedToLoad) {
                    for (var i = 0; i < meshContents.length; i++) {

                        var geoLength = this._meshManager.processMeshData(meshContents[i], event.data.objectBaseMaterialMap, event.data.appearanceOverrides, event.data.blockId, event.data.pageId);

                        if (this._pageLength[event.data.blockId + "_" + event.data.pageId] == null) {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] = geoLength;
                        }
                        else {
                            this._pageLength[event.data.blockId + "_" + event.data.pageId] += geoLength;
                        }

                        this._dataLength += geoLength;

                        if (this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {
                            this._displayDataStatus[event.data.blockId][event.data.pageId] = 2;
                        }
                    }

                    this._processInstanceContents(instanceContents, event.data.blockId, event.data.pageId);

                    if (instanceContents.length > 0 && this._tasksForCompletePage[event.data.blockId][event.data.pageId] == 0) {
                        this._displayDataStatus[event.data.blockId][event.data.pageId] = 2;
                    }
                }
                else {

                    this._displayDataStatus[event.data.blockId][event.data.pageId] = 0;
                }

                for (var i = 0; i < annotations.markedPoints.length; i++) {
                    this._annotationManager.processMarkedPointData(annotations.markedPoints[i], event.data.blockId, event.data.pageId);
                }

                this._meshManager.meshesDirty = true;
            }

            this._releaseWorkerForEvent(event);

            numberOfProcessedData++;
        }

        return numberOfProcessedData;
    }

    private abandonCurrentWorkerResults() {

        this._displayRequestGeneration++;
        const completedEvents = this._processedWorkerEvents;
        const pendingTasks = this._pool?.takePendingTasks() ?? [];
        this._processedWorkerEvents = [];
        this._finWorkerEvents = [];

        if (this._pool) {
            const abandonedRequests: any[] = completedEvents.map(event => ({
                BlockId: event?.data?.blockId,
                PageId: event?.data?.pageId,
            }));

            // Completed results normally return their worker to the pool while
            // being processed. If the results are abandoned first, those worker
            // slots otherwise remain permanently recorded as in use.
            for (const event of completedEvents) {
                this._releaseWorkerForEvent(event);
            }

            // Any currently running workers will have their results ignored.
            for (var x = 0; x < this._pool._inUseWorkers.length; x++) {

                var inUseWorker = this._pool._inUseWorkers[x];

                var trackedPageRequests = (inUseWorker.pageRequests != null && inUseWorker.pageRequests.length > 0)
                    ? inUseWorker.pageRequests
                    : [{ BlockId: inUseWorker.blockId, PageId: inUseWorker.pageId }];

                abandonedRequests.push(...trackedPageRequests);
            }

            for (const pendingTask of pendingTasks) {
                const trackedPageRequests = Array.isArray(pendingTask?.PageRequests) && pendingTask.PageRequests.length > 0
                    ? pendingTask.PageRequests
                    : [{ BlockId: pendingTask?.BlockId, PageId: pendingTask?.PageId }];
                abandonedRequests.push(...trackedPageRequests);
            }

            for (const pageRequest of abandonedRequests) {
                var blockId = pageRequest?.BlockId;
                var pageId = pageRequest?.PageId;

                if (blockId == null || pageId == null) {
                    continue;
                }

                if (this._tasksForCompletePage != null &&
                    this._tasksForCompletePage[blockId] != null &&
                    this._tasksForCompletePage[blockId][pageId] != null) {
                    this._tasksForCompletePage[blockId][pageId] = 0;
                }

                if (this._displayDataStatus != null &&
                    this._displayDataStatus[blockId] != null &&
                    this._displayDataStatus[blockId][pageId] != null
                ) {
                    this._displayDataStatus[blockId][pageId] = 0;
                }

                if (this._displayRegionDataStatus != null &&
                    this._displayRegionDataStatus[blockId] != null &&
                    this._displayRegionDataStatus[blockId][pageId] != null
                ) {
                    this._displayRegionDataStatus[blockId][pageId] = 0;
                }
            }
        }
    }
}

class Pool {
    public static testThreading: boolean = true;
    private _poolSize: number;
    private _taskCompletionCB: (workerNumber: number, event: MessageEvent) => void;
    public _availableWorkers: DisplayWorker[] = [];
    public _inUseWorkers: DisplayWorker[] = [];
    private _pendingTasks: any[] = [];
    private _assetGeometryOwnership: Interfaces.Interfaces.IAssetGeometryOwnershipSnapshot | null = null;

    constructor(size: number, useOctreeData : boolean, taskCompletionCB: (workerNumber: number, event: MessageEvent) => void) {
        this._poolSize = size;
        this._taskCompletionCB = taskCompletionCB;

        if (useOctreeData) {
            DisplayWorker.ProcessingMode = 1;
        }
        else {
            DisplayWorker.ProcessingMode = 0;
        }

        this._createWorkers();
    }

    private _createWorkers() {
        for (var i = 1; i < this._poolSize + 1; i++) {
            this._availableWorkers.push(this._createWorker(i));
        }
    }

    private _createWorker(slotNumber: number): DisplayWorker {
        const worker = new DisplayWorker(slotNumber, (i, event) => {
            this._taskCompletionCB(i, event);
        });
        if (this._assetGeometryOwnership) {
            worker.setAssetGeometryOwnership(this._assetGeometryOwnership);
        }
        return worker;
    }

    public setAssetGeometryOwnership(ownership: Interfaces.Interfaces.IAssetGeometryOwnershipSnapshot): void {
        this._assetGeometryOwnership = ownership;
        for (const worker of [...this._availableWorkers, ...this._inUseWorkers]) {
            worker.setAssetGeometryOwnership(ownership);
        }
    }

    dispose() {

        for (var x = 0; x < this._availableWorkers.length; x++) {

            this._availableWorkers[x].terminate();
        }

        for (var x = 0; x < this._inUseWorkers.length; x++) {

            this._inUseWorkers[x].terminate();
        }

        this._availableWorkers = [];
        this._inUseWorkers = [];
        this._pendingTasks = [];
    }

    reset() {
        this.dispose();
        this._createWorkers();
    }

    public isHighestPriorityInProgressWorker(workerNumber: number) {

        var highestPriority = Number.MAX_VALUE;
        var workerPriority = Number.MAX_VALUE;

        for (var i = 0; i < this._inUseWorkers.length; i++) {

            if (this._inUseWorkers[i].workerNumber == workerNumber) {

                workerPriority = this._inUseWorkers[i].workerContentPriority;
            }

            highestPriority = Math.min(highestPriority, this._inUseWorkers[i].workerContentPriority);
        }

        if (highestPriority != workerPriority) {
            return false;
        }

        return true;
    }
    public addTask(displayParameters: any) {
        this._pendingTasks.push(displayParameters);
        this.processWaitingTasks();
    }

    public takePendingTasks(): any[] {
        return this._pendingTasks.splice(0, this._pendingTasks.length);
    }

    public makeWorkerAvailable(workerNumber: number) {
        for (var i = 0; i < this._inUseWorkers.length; i++) {
            if (this._inUseWorkers[i].workerNumber == workerNumber) {
                var finishedWorker = this._inUseWorkers.splice(i, 1);
                this._availableWorkers.push(finishedWorker[0]);

                break;
            }
        }

        this.processWaitingTasks();
    }

    private processWaitingTasks() {
        while (this._availableWorkers.length > 0 && this._pendingTasks.length > 0) {
            var availableWorker = this._availableWorkers.pop();
            this._inUseWorkers.push(availableWorker);

            var pendingTask = this._pendingTasks.shift();

            availableWorker.workerContentPriority = pendingTask.ContentDisplayPriority;
            availableWorker.run(pendingTask);
        }
    }
}

class RegionOctant {
    public ChildOctants: RegionOctant[];
    public ChildContentIDs: Array<number>;
    public RegionFileNames: Array<string>;
    public Content: Region[];
    public Parent: RegionOctant;

    public ID: number;
    public Minimum: BABYLON.Vector3;
    public Maximum: BABYLON.Vector3;
    public Depth: number;

    public RegionName: string;
    constructor() {
        this.ChildOctants = [];
        this.ChildContentIDs = new Array<number>();
        this.RegionFileNames = new Array<string>();
        this.Content = [];
        this.Parent = null;
    }

    dispose() {
        delete this.ChildOctants;
        delete this.ChildContentIDs;
        delete this.Content;
    }
}

class LODLevelInfo {
    public LODLevel: string;
    public VertexCount: number;
}

class Region {
    public Mesh: BABYLON.InstancedMesh;
    public Minimum: BABYLON.Vector3;
    public Maximum: BABYLON.Vector3;
    public ID: number;
}

class DisplayWorker {

    public static DisplayRequestURL: string = "";
    public static OctreeRequestURL: string = "";
    public static DisplayDecoderURL: string = "";
    public static ProjectID: string = "";
    public static ActivatedNamedLists: any = [];
    public static AllNamedLists: any = [];
    public static UseDefaultMaterial: boolean = false;
    public static UseXRayMaterial: boolean = false;
    public static LodLevel: number = 0;
    public static TransformationX: number = 0;
    public static TransformationY: number = 0;
    public static TransformationZ: number = 0;
    public static jobCount = 1;
    public static ProcessingMode = 0;
    public static DataRequestCallback: Function;

    public blockId : string;
    public pageId : string;
    public pageRequests: Array<{ BlockId: string; PageId: string }> = [];
    private _webWorker: Worker;

    public workerNumber: number;
    public workerContentPriority: number;

    constructor(workerNumber: number, taskCompletionCB: (workerNumber: number, event: MessageEvent) => void) {

        this._webWorker = new Worker(new URL('../workers/WebWorker.ts', import.meta.url), { type: 'module' });

        this._webWorker.onmessage = (event) => {
            if (handleWorkerAuthRefreshMessage(this._webWorker, event)) {
                return;
            }

            taskCompletionCB(this.workerNumber, event);
        };

        this._webWorker.onerror = (event) => {
            taskCompletionCB(this.workerNumber, {
                data: {
                    workerNumber: this.workerNumber,
                    blockId: this.blockId,
                    pageId: this.pageId,
                    error: event.message || 'Display worker error'
                }
            } as MessageEvent);
        };
    }

    public run(workerData: any) {

        this.blockId = workerData.BlockId;
        this.pageId = workerData.PageId;
        this.pageRequests = Array.isArray(workerData.PageRequests) && workerData.PageRequests.length > 0
            ? workerData.PageRequests.map((pageRequest: any) => ({ BlockId: pageRequest.BlockId, PageId: pageRequest.PageId }))
            : [{ BlockId: workerData.BlockId, PageId: workerData.PageId }];
        this.workerNumber = DisplayWorker.jobCount;
        DisplayWorker.jobCount++;

        var worker = this._webWorker;

        workerData.WebWorkerNumber = this.workerNumber;
        workerData.AuthContext = getCurrentWorkerAuthContext();

        if (!DisplayWorker.DataRequestCallback) {
            worker.postMessage([workerData]);
        } else {
            var requestedData: Data.GraphicData.WebWorkerGraphicData = DisplayWorker.DataRequestCallback(workerData);

            if (requestedData != null) {
                requestedData.DisplayGeneration = workerData.DisplayGeneration;
                requestedData.SourceAssetSetId = workerData.SourceAssetSetId;
                requestedData.GeometryOwnershipRevision = workerData.GeometryOwnershipRevision;
                requestedData.GeometryOwnershipEpoch = workerData.GeometryOwnershipEpoch;
            }

            worker.postMessage([requestedData]);
        }
    }

    public setAssetGeometryOwnership(ownership: Interfaces.Interfaces.IAssetGeometryOwnershipSnapshot): void {
        this._webWorker.postMessage([{
            Type: 4,
            GeometryOwnershipRevision: ownership.revision,
            OwnerByAssetId: ownership.ownerByAssetId,
            DefaultUnownedGeometryHidden: ownership.defaultUnownedGeometryHidden,
            FallbackSourceAssetSetIds: ownership.fallbackSourceAssetSetIds,
        }]);
    }

    public terminate() {
        this._webWorker.terminate();
    }

    public close() {
        this._webWorker.postMessage([{ Type: 1 }]);
    }
}

export default DisplayManager;
